import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { resolveFrontendApiBase } from './utils/apiBase';
import axios from './utils/accountApi';
import { Button, Dropdown, Space, Tooltip, Popconfirm, message, Modal } from 'antd';
import { EditOutlined, AudioOutlined, ExportOutlined, FormOutlined, TranslationOutlined, UploadOutlined, CheckOutlined, ReloadOutlined, ArrowLeftOutlined, BranchesOutlined, QuestionCircleOutlined, SearchOutlined, PlusOutlined, BulbOutlined, ExclamationCircleOutlined, FileImageOutlined, HistoryOutlined, EyeOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { TranslationMilestoneTrackerProps } from './components/TranslationMilestoneTracker';
import LanguageProgressChips from './components/LanguageProgressChips';
import FeatherSpinner from './components/FeatherSpinner';
import { doc, getDoc } from 'firebase/firestore';
import { db } from './firebaseConfig';
import { Book, RecentExportEntry, TranslationProgressMap } from './types';
import { RELATIONSHIP_UI_ENABLED } from './featureFlags';
import { splitLanguagePair } from './utils/languagePairs';
import {
  canonicalizeLanguageKey,
  canonicalizeLanguageName,
  canonicalizeTranslationProgressMap,
  languagesMatch,
} from './utils/languageIdentity';
import {
  isMissingTranslatedMetadataValue,
} from './utils/localizedMetadataDisplay';
import { resolveAccountTerms, resolveBookTerms, resolveSeriesTerms } from './utils/glossaryKeys';
import {
  hasCompletedProofreadProgressForRow,
  hasPendingProofreadReviewForRow,
} from './utils/proofreadState';
import {
  buildSourceReadinessItems,
  type SourceReadinessItem,
} from './utils/sourceReadiness';

interface BookDashboardTabProps {
  showTranslationProgress?: boolean;
  translationProgress?: TranslationProgressMap;
  translationLanguages?: string[];
  onSelectTranslationLanguage?: (language: string) => void;
  onEditMetadata: () => void;
  onCreateAudiobook: () => void;
  onExportBook: () => void;
  onUploadChapters: () => void;
  onTranslateBook: () => void;
  onProofreadBook: () => void;
  onAddLanguage?: () => void;
  selectedLanguage?: string;
  recentExports: RecentExportEntry[];
  onDownloadRecentExport: (entry: RecentExportEntry) => Promise<void>;
  onRefreshProgress?: () => void;
  isRefreshingProgress?: boolean;
  userId?: string;
  bookId?: string;
  translationTrackerData?: TranslationMilestoneTrackerProps | null;
  onOpenMetadataForLanguage?: (language: string) => void;
  onOpenMetadataTranslateForLanguage?: (language: string) => void;
  onOpenKeywordSuggestionsForLanguage?: (language: string) => void;
  onOpenCoverTranslationForLanguage?: (language: string) => void;
  onOpenCoverHistoryForLanguage?: (language: string) => void;
  onOpenProofreadForLanguage?: (language: string) => void;
  onApplyPendingProofreadReviewsForLanguage?: (language: string) => void;
  applyingPendingProofreadLanguage?: string | null;
  onOpenGlossaryScanForLanguage?: (language: string) => void;
  onReturnToOriginalDashboard?: () => void;
  onSelectDashboardLanguage?: (language: string) => void;
  availableLanguages?: string[];
  bookLanguage?: string;
  translationPairs?: string[];
  keywordTranslations?: Record<string, string[]>;
  neverTranslateWords?: Record<string, string[]>;
  alwaysTranslateWords?: Record<string, string[]>;
  accountNeverTranslateWords?: Record<string, string[]>;
  accountAlwaysTranslateWords?: Record<string, string[]>;
  seriesNeverTranslateWords?: Record<string, string[]>;
  seriesAlwaysTranslateWords?: Record<string, string[]>;
  languageCovers?: { [language: string]: string };
  book?: Book;
  onOpenRelationshipAnalysis?: () => void;
  onOpenCharactersTab?: () => void;
  isUploadDisabled?: boolean;
  uploadDisabledReason?: string;
  onOriginalCoverUpload?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  hasTitle?: boolean;
  hasAuthor?: boolean;
  hasBlurb?: boolean;
  hasGenres?: boolean;
  hasKeywords?: boolean;
  hasCover?: boolean;
  frenchDialogueLegacyEligibilityByPair?: Record<
    string,
    {
      eligible: boolean;
      reasonCode: string;
      reasonDetail?: string;
      pair?: string;
      alreadyRan?: boolean;
      inProgress?: boolean;
      rulesetVersion?: string;
      rulesetEligibility?: Record<
        string,
        {
          eligible: boolean;
          reasonCode: string;
          reasonDetail?: string;
          rulesetVersion?: string;
        }
      >;
      discountedRetranslation?: {
        eligible: boolean;
        reasonCode: string;
        reasonDetail?: string;
        requiredCredits?: number;
        discountedCredits?: number;
        discountPercent?: number;
        runId?: string;
        inProgress?: boolean;
        mode?: 'initial' | 'retry_failed_only';
        offerRootRunId?: string | null;
        attemptNumberNext?: number;
        chaptersTotalOriginal?: number;
        chaptersRemaining?: number;
        chaptersFailedRemaining?: number;
        chapterGuidsToQueue?: string[];
      };
    }
  >;
  onFetchFrenchDialogueLegacyEligibility?: (params: {
    pair: string;
    targetLanguage: string;
    force?: boolean;
  }) => Promise<
    | {
        eligible: boolean;
        reasonCode: string;
        reasonDetail?: string;
        pair?: string;
        alreadyRan?: boolean;
        inProgress?: boolean;
        rulesetVersion?: string;
        rulesetEligibility?: Record<
          string,
          {
            eligible: boolean;
            reasonCode: string;
            reasonDetail?: string;
            rulesetVersion?: string;
          }
        >;
        discountedRetranslation?: {
          eligible: boolean;
          reasonCode: string;
          reasonDetail?: string;
          requiredCredits?: number;
          discountedCredits?: number;
          discountPercent?: number;
          runId?: string;
          inProgress?: boolean;
          mode?: 'initial' | 'retry_failed_only';
          offerRootRunId?: string | null;
          attemptNumberNext?: number;
          chaptersTotalOriginal?: number;
          chaptersRemaining?: number;
          chaptersFailedRemaining?: number;
          chapterGuidsToQueue?: string[];
        };
      }
    | null
  >;
  onRunFrenchDialogueLegacyCorrection?: (params: {
    pair: string;
    targetLanguage: string;
    chapterGuid?: string;
    chapterName?: string;
  }) => void;
  onDashboardOverflowActionsChange?: (actions: DashboardUtilityAction[]) => void;
}

const API_BASE = resolveFrontendApiBase();
const TERM_SCAN_SUCCESS_STATES = new Set(['completed', 'succeeded', 'finished']);
const TERM_SCAN_IN_FLIGHT_STATES = new Set(['queued', 'running']);

const normalizeLangKey = (value?: string | null): string => canonicalizeLanguageKey(value);
const exportMatchesLanguage = (entry: RecentExportEntry, language: string): boolean => {
  const targetKey = normalizeLangKey(language);
  if (!targetKey) {
    return false;
  }
  const candidates = [entry.languageKey, entry.version]
    .map(value => String(value || '').trim())
    .filter(Boolean);
  return candidates.some(candidate => {
    const { targetLang } = splitLanguagePair(candidate);
    return normalizeLangKey(targetLang || candidate) === targetKey;
  });
};
const hasDashboardMetadataValue = (value: unknown): boolean => !isMissingTranslatedMetadataValue(value);

type DashboardStep = {
  key: string;
  label: string;
  title?: string;
  onClick: () => void;
  icon: React.ReactNode;
  cta: string;
  ctaOnClick?: () => void;
  disabled?: boolean;
  progressPercent?: number;
  detail?: string;
  isBusy?: boolean;
  busyText?: string;
  highlightAction?: boolean;
  pendingReviewCount?: number;
  isComplete?: boolean;
};

type LanguageStatusCardViewModel = {
  language: string;
  sortOrder: number;
  coverUrl?: string;
  translatedCount: number;
  totalChapters: number;
  proofreadCount: number;
  pendingProofreadReviewCount: number;
  metadataDone: boolean;
  keywordsDone: boolean;
  coverDone: boolean;
  audiobookDone: boolean;
  glossaryReady: boolean;
  glossaryScanInFlight?: boolean;
  glossaryScanNeedsReview?: boolean;
  onOpenWorkspace: () => void;
  onOpenGlossary?: () => void;
  onOpenMetadata?: () => void;
  onOpenKeywords?: () => void;
  onOpenCover?: () => void;
  onApplyPending?: () => void;
  isApplyingPending?: boolean;
};

type LanguageOverviewBucket = 'in_progress' | 'not_started' | 'complete';

const ACTIVE_LANGUAGE_WORKSPACE_VISIBLE_LIMIT = 4;
const COMPACT_ACTIVE_LANGUAGE_WORKSPACE_VISIBLE_LIMIT = 2;
const COMPACT_ACTIVE_LANGUAGE_WORKSPACE_QUERY = '(max-width: 1560px), (max-height: 1200px)';
const LANGUAGE_QUEUE_TAG_MAX_ROWS = 2;
const LANGUAGE_QUEUE_TAG_GAP_PX = 4;
const LANGUAGE_QUEUE_TAG_MAX_WIDTH_PX = 170;
const LANGUAGE_QUEUE_TAG_MIN_WIDTH_PX = 24;
const LANGUAGE_QUEUE_TAG_HORIZONTAL_PADDING_PX = 12;

const estimateLanguageQueueTagWidth = (label: string): number => {
  const estimatedTextWidth = label.length * 6.4;
  return Math.min(
    LANGUAGE_QUEUE_TAG_MAX_WIDTH_PX,
    Math.max(
      LANGUAGE_QUEUE_TAG_MIN_WIDTH_PX,
      Math.ceil(estimatedTextWidth + LANGUAGE_QUEUE_TAG_HORIZONTAL_PADDING_PX),
    ),
  );
};

const canFitLanguageQueueTags = (widths: number[], availableWidth: number, gap: number): boolean => {
  if (!widths.length) {
    return true;
  }
  if (availableWidth <= 0) {
    return false;
  }
  let rowCount = 1;
  let rowWidth = 0;
  widths.forEach(width => {
    const nextWidth = rowWidth > 0 ? rowWidth + gap + width : width;
    if (rowWidth > 0 && nextWidth > availableWidth) {
      rowCount += 1;
      rowWidth = width;
      return;
    }
    rowWidth = nextWidth;
  });
  return rowCount <= LANGUAGE_QUEUE_TAG_MAX_ROWS;
};

const getActiveLanguageWorkspaceVisibleLimit = (): number => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return ACTIVE_LANGUAGE_WORKSPACE_VISIBLE_LIMIT;
  }
  return window.matchMedia(COMPACT_ACTIVE_LANGUAGE_WORKSPACE_QUERY).matches
    ? COMPACT_ACTIVE_LANGUAGE_WORKSPACE_VISIBLE_LIMIT
    : ACTIVE_LANGUAGE_WORKSPACE_VISIBLE_LIMIT;
};

const useActiveLanguageWorkspaceVisibleLimit = (): number => {
  const [visibleLimit, setVisibleLimit] = useState(getActiveLanguageWorkspaceVisibleLimit);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }
    const mediaQuery = window.matchMedia(COMPACT_ACTIVE_LANGUAGE_WORKSPACE_QUERY);
    const updateVisibleLimit = () => {
      setVisibleLimit(
        mediaQuery.matches
          ? COMPACT_ACTIVE_LANGUAGE_WORKSPACE_VISIBLE_LIMIT
          : ACTIVE_LANGUAGE_WORKSPACE_VISIBLE_LIMIT,
      );
    };

    updateVisibleLimit();
    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', updateVisibleLimit);
      return () => mediaQuery.removeEventListener('change', updateVisibleLimit);
    }
    mediaQuery.addListener(updateVisibleLimit);
    return () => mediaQuery.removeListener(updateVisibleLimit);
  }, []);

  return visibleLimit;
};

const LANGUAGE_OVERVIEW_BUCKETS: Array<{
  key: LanguageOverviewBucket;
  title: string;
  helperText: string;
}> = [
  {
    key: 'in_progress',
    title: 'In progress',
    helperText: 'Continue active or attention-needed language workflows.',
  },
  {
    key: 'not_started',
    title: 'Not started',
    helperText: 'Languages added but not yet worked.',
  },
  {
    key: 'complete',
    title: 'Complete',
    helperText: 'Ready languages, shown quieter.',
  },
];

export type DashboardUtilityAction = {
  key: string;
  signature?: string;
  label: string;
  icon?: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  disabledReason?: string;
  tourId?: string;
  items?: Array<{ key: string; label: string }>;
  onItemClick?: (key: string) => void;
};

type DashboardStatusTone = 'ready' | 'attention' | 'muted' | 'blocked';

type DashboardWorkstreamRow = {
  key: string;
  title: string;
  status: string;
  statusTone: DashboardStatusTone;
  detail: string;
  actionLabel?: string;
  onAction?: () => void;
  disabled?: boolean;
  progressPercent?: number;
  dismissKey?: string;
};

type DashboardWorkstream = {
  key: string;
  title: string;
  description: string;
  rows: DashboardWorkstreamRow[];
};

const formatDashboardCount = (value: number): string => Math.max(0, value || 0).toLocaleString();
const NOT_STARTED_MARKET_PRIORITY: Record<string, number> = {
  German: 10,
  French: 20,
  Italian: 30,
  'Brazilian Portuguese': 40,
  'European Spanish': 50,
  Dutch: 60,
  'Latin American Spanish': 70,
  Japanese: 80,
  Korean: 90,
  'Simplified Chinese': 100,
  'European Portuguese': 110,
  Polish: 120,
  Russian: 130,
  Swedish: 140,
  Ukrainian: 150,
  Turkish: 160,
  Hindi: 170,
  Thai: 180,
  'Traditional Chinese': 190,
  Norwegian: 200,
  Hebrew: 210,
  Catalan: 220,
  Serbian: 230,
  Afrikaans: 240,
  'British English': 250,
};

const getLanguageProgressRows = (model: LanguageStatusCardViewModel): Array<{
  label: string;
  value: string;
  tone: 'default' | 'ready' | 'attention';
}> => {
  const total = Math.max(0, model.totalChapters || 0);
  const translated = Math.max(0, model.translatedCount || 0);
  const proofread = Math.max(0, model.proofreadCount || 0);
  const rows: Array<{ label: string; value: string; tone: 'default' | 'ready' | 'attention' }> = [
    {
      label: 'Translation',
      value: `${formatDashboardCount(translated)}/${formatDashboardCount(total)}`,
      tone: total > 0 && translated >= total ? 'ready' : translated > 0 ? 'attention' : 'default',
    },
  ];

  if (model.pendingProofreadReviewCount > 0) {
    rows.push({
      label: 'Proofread',
      value: `${formatDashboardCount(model.pendingProofreadReviewCount)} pending`,
      tone: 'attention',
    });
  } else if (proofread >= total && total > 0) {
    rows.push({
      label: 'Proofread',
      value: `${formatDashboardCount(proofread)}/${formatDashboardCount(total)}`,
      tone: 'ready',
    });
  } else {
    rows.push({
      label: 'Proofread',
      value: `${formatDashboardCount(proofread)}/${formatDashboardCount(total)}`,
      tone: proofread > 0 ? 'attention' : 'default',
    });
  }

  return rows;
};

const getLanguageReadinessChips = (model: LanguageStatusCardViewModel): Array<{
  label: string;
  tone: 'ready' | 'needed' | 'attention' | 'muted';
  onClick?: () => void;
}> => {
  const chips: Array<{ label: string; tone: 'ready' | 'needed' | 'attention' | 'muted'; onClick?: () => void }> = [];
  if (model.glossaryScanInFlight) {
    chips.push({ label: 'Glossary running', tone: 'attention', onClick: model.onOpenGlossary });
  } else if (model.glossaryScanNeedsReview) {
    chips.push({ label: 'Glossary review suggested', tone: 'attention', onClick: model.onOpenGlossary });
  } else if (!model.glossaryReady) {
    chips.push({ label: 'Glossary suggested', tone: 'attention', onClick: model.onOpenGlossary });
  }
  chips.push({
    label: `Metadata ${model.metadataDone ? 'ready' : 'suggested'}`,
    tone: model.metadataDone ? 'ready' : 'attention',
    onClick: model.onOpenMetadata,
  });
  chips.push({
    label: `Keywords ${model.keywordsDone ? 'ready' : 'suggested'}`,
    tone: model.keywordsDone ? 'ready' : 'attention',
    onClick: model.onOpenKeywords,
  });
  chips.push({
    label: `Cover ${model.coverDone ? 'ready' : 'suggested'}`,
    tone: model.coverDone ? 'ready' : 'attention',
    onClick: model.onOpenCover,
  });
  chips.push({ label: `Audiobook ${model.audiobookDone ? 'ready' : 'not started'}`, tone: model.audiobookDone ? 'ready' : 'muted' });
  return chips;
};

const getLanguageNeeds = (model: LanguageStatusCardViewModel): string[] => {
  const needs: string[] = [];
  if (model.pendingProofreadReviewCount > 0) {
    needs.push('Proofread review');
  }
  if (model.glossaryScanInFlight) {
    needs.push('Glossary scan running');
  } else if (model.glossaryScanNeedsReview) {
    needs.push('Glossary review');
  } else if (!model.glossaryReady) {
    needs.push('Glossary');
  }
  if (!model.metadataDone) needs.push('Metadata');
  if (!model.keywordsDone) needs.push('keywords');
  if (!model.coverDone) needs.push('cover');
  if (!model.audiobookDone) needs.push('audiobook');
  return needs;
};

const getLanguageNextActionText = (model: LanguageStatusCardViewModel): string => {
  const total = Math.max(0, model.totalChapters || 0);
  const translated = Math.max(0, model.translatedCount || 0);
  const needs = getLanguageNeeds(model);
  if (model.pendingProofreadReviewCount > 0) {
    return `${formatDashboardCount(model.pendingProofreadReviewCount)} proofread review${model.pendingProofreadReviewCount === 1 ? '' : 's'} ready`;
  }
  if (model.glossaryScanNeedsReview) {
    return 'Review glossary scan';
  }
  if (model.glossaryScanInFlight) {
    return 'Glossary scan running';
  }
  if (needs.length) {
    return `Suggested: ${needs.slice(0, 3).join(', ')}${needs.length > 3 ? ` +${needs.length - 3}` : ''}`;
  }
  if (total > 0 && translated < total) {
    return 'Continue translation';
  }
  return 'Ready for review';
};

const getLanguageOverviewBucket = (model: LanguageStatusCardViewModel): LanguageOverviewBucket => {
  const total = Math.max(0, model.totalChapters || 0);
  const translated = Math.max(0, model.translatedCount || 0);
  const proofread = Math.max(0, model.proofreadCount || 0);
  const translationComplete = total > 0 ? translated >= total : translated > 0;
  const proofreadComplete = total > 0 ? proofread >= total : proofread > 0;
  const hasStartedWork = translated > 0 || proofread > 0 || model.glossaryReady || model.metadataDone || model.keywordsDone || model.coverDone || model.audiobookDone;

  if (model.pendingProofreadReviewCount > 0 || model.glossaryScanInFlight || model.glossaryScanNeedsReview) {
    return 'in_progress';
  }

  if (translationComplete && (!model.glossaryReady || !model.metadataDone || !model.keywordsDone || !model.coverDone)) {
    return 'in_progress';
  }

  if (translationComplete && proofreadComplete && model.glossaryReady && model.metadataDone && model.keywordsDone && model.coverDone) {
    return 'complete';
  }

  if (hasStartedWork) {
    return 'in_progress';
  }

  return 'not_started';
};

const sortLanguageCards = (cards: LanguageStatusCardViewModel[]) =>
  [...cards].sort((a, b) => {
    if (a.sortOrder !== b.sortOrder) {
      return a.sortOrder - b.sortOrder;
    }
    return a.language.localeCompare(b.language, undefined, { sensitivity: 'base' });
  });

const getActiveLanguagePriority = (card: LanguageStatusCardViewModel): number => {
  if (card.pendingProofreadReviewCount > 0) return 0;
  if (card.glossaryScanNeedsReview) return 1;
  if (card.glossaryScanInFlight) return 2;
  const total = Math.max(0, card.totalChapters || 0);
  const translated = Math.max(0, card.translatedCount || 0);
  if (translated > 0 && (total === 0 || translated < total)) return 3;
  return 4;
};

const sortActiveLanguageCards = (cards: LanguageStatusCardViewModel[]) =>
  [...cards].sort((a, b) => {
    const priorityDifference = getActiveLanguagePriority(a) - getActiveLanguagePriority(b);
    if (priorityDifference !== 0) return priorityDifference;
    if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
    return a.language.localeCompare(b.language, undefined, { sensitivity: 'base' });
  });

const sortNotStartedLanguageCards = (cards: LanguageStatusCardViewModel[]) =>
  [...cards].sort((a, b) => {
    const priorityA = NOT_STARTED_MARKET_PRIORITY[canonicalizeLanguageName(a.language)] ?? Number.MAX_SAFE_INTEGER;
    const priorityB = NOT_STARTED_MARKET_PRIORITY[canonicalizeLanguageName(b.language)] ?? Number.MAX_SAFE_INTEGER;
    if (priorityA !== priorityB) {
      return priorityA - priorityB;
    }
    if (a.sortOrder !== b.sortOrder) {
      return a.sortOrder - b.sortOrder;
    }
    return a.language.localeCompare(b.language, undefined, { sensitivity: 'base' });
  });

const groupLanguageCardsByBucket = (languageCards: LanguageStatusCardViewModel[]) => {
  const groups = new Map<LanguageOverviewBucket, LanguageStatusCardViewModel[]>();
  LANGUAGE_OVERVIEW_BUCKETS.forEach(bucket => groups.set(bucket.key, []));
  languageCards.forEach(card => {
    const bucket = getLanguageOverviewBucket(card);
    groups.get(bucket)?.push(card);
  });
  return LANGUAGE_OVERVIEW_BUCKETS.map(bucket => ({
    ...bucket,
    cards: bucket.key === 'not_started'
      ? sortNotStartedLanguageCards(groups.get(bucket.key) || [])
      : bucket.key === 'in_progress'
        ? sortActiveLanguageCards(groups.get(bucket.key) || [])
        : sortLanguageCards(groups.get(bucket.key) || []),
  }));
};

const buildDashboardBucketCounts = (groups: ReturnType<typeof groupLanguageCardsByBucket>) =>
  LANGUAGE_OVERVIEW_BUCKETS.map(bucket => ({
    ...bucket,
    count: groups.find(group => group.key === bucket.key)?.cards.length || 0,
  }));

const renderProgressBar = (percent?: number) => {
  if (typeof percent !== 'number') {
    return null;
  }
  const bounded = Math.min(100, Math.max(0, Math.round(percent)));
  return (
    <Tooltip title={`${bounded}% complete`}>
      <div className="dashboard-progress-bar" aria-label={`${bounded}% complete`}>
        <div className="dashboard-progress-bar-fill" style={{ width: `${bounded}%` }} />
      </div>
    </Tooltip>
  );
};

const LanguageStatusCard: React.FC<{
  model: LanguageStatusCardViewModel;
  bucket: LanguageOverviewBucket;
}> = ({ model, bucket }) => {
  const isNotStartedCard = bucket === 'not_started';
  const progressRows = getLanguageProgressRows(model);
  const readinessChips = getLanguageReadinessChips(model);
  const nextActionText = getLanguageNextActionText(model);
  const isPrimaryCard = bucket === 'in_progress';
  const languageInitials = model.language.slice(0, 2).toUpperCase();

  if (isNotStartedCard) {
    return (
      <div className={`dashboard-language-card dashboard-language-card--${bucket} dashboard-language-card--compact`}>
        <div className="dashboard-language-card-top">
          <div className="dashboard-language-cover dashboard-language-cover--compact">
            {model.coverUrl ? (
              <img src={model.coverUrl} alt={`${model.language} cover`} />
            ) : (
              <span>{languageInitials}</span>
            )}
          </div>
          <div className="dashboard-language-card-copy">
            <h3>{model.language}</h3>
            <p>{formatDashboardCount(model.totalChapters)} chapter{model.totalChapters === 1 ? '' : 's'} available</p>
          </div>
          <Button type="default" size="small" className="dashboard-language-card-open" onClick={model.onOpenWorkspace}>
            Open
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={`dashboard-language-card dashboard-language-card--${bucket}`}>
      <div className="dashboard-language-card-top">
        <div className="dashboard-language-cover">
          {model.coverUrl ? (
            <img src={model.coverUrl} alt={`${model.language} cover`} />
          ) : (
            <span>{languageInitials}</span>
          )}
        </div>
        <div className="dashboard-language-card-copy">
          <h3>{model.language}</h3>
          <p>{nextActionText}</p>
        </div>
        <Button
          type={isPrimaryCard && !(model.pendingProofreadReviewCount > 0 && model.onApplyPending) ? 'primary' : 'default'}
          size="small"
          className="dashboard-language-card-open"
          onClick={model.onOpenWorkspace}
        >
          Open
        </Button>
      </div>
      <div className="dashboard-language-progress-rows">
        {progressRows.map(row => (
          <div key={row.label} className={`dashboard-language-progress-row dashboard-language-progress-row--${row.tone}`}>
            <span>{row.label}</span>
            <strong>{row.value}</strong>
          </div>
        ))}
      </div>
      <div className="dashboard-language-chip-row" aria-label={`${model.language} readiness`}>
        {readinessChips.map(chip => {
          const className = `dashboard-readiness-chip dashboard-language-readiness-chip dashboard-readiness-chip--${chip.tone}${chip.onClick ? ' dashboard-language-readiness-chip--clickable' : ''}`;
          if (chip.onClick) {
            return (
              <button
                key={chip.label}
                type="button"
                className={className}
                onClick={chip.onClick}
                aria-label={`${model.language}: ${chip.label}`}
              >
                {chip.label}
              </button>
            );
          }
          return (
            <span key={chip.label} className={className}>
              {chip.label}
            </span>
          );
        })}
      </div>
      {model.pendingProofreadReviewCount > 0 && model.onApplyPending ? (
        <div className="dashboard-language-card-actions">
          <Button
            type={isPrimaryCard ? 'primary' : 'default'}
            onClick={model.onApplyPending}
            loading={model.isApplyingPending}
          >
            Apply pending
          </Button>
        </div>
      ) : null}
    </div>
  );
};

const CompactLanguageQueueRow: React.FC<{
  model: LanguageStatusCardViewModel;
  bucket: LanguageOverviewBucket;
}> = ({ model, bucket }) => {
  const progressRows = getLanguageProgressRows(model);
  const readinessChips = getLanguageReadinessChips(model);
  const tagsRef = useRef<HTMLDivElement | null>(null);
  const prioritizedReadinessChips = useMemo(
    () => [
      ...readinessChips.filter(chip => chip.onClick),
      ...readinessChips.filter(chip => !chip.onClick),
    ],
    [readinessChips],
  );
  const [visibleChipCount, setVisibleChipCount] = useState(() => (
    bucket === 'not_started' ? 0 : prioritizedReadinessChips.length
  ));
  useEffect(() => {
    if (bucket === 'not_started' || !prioritizedReadinessChips.length) {
      setVisibleChipCount(0);
      return;
    }

    const element = tagsRef.current;
    if (!element) {
      setVisibleChipCount(prioritizedReadinessChips.length);
      return;
    }

    const updateVisibleChipCount = () => {
      const availableWidth = element.clientWidth;
      if (availableWidth <= 0) {
        setVisibleChipCount(prioritizedReadinessChips.length);
        return;
      }
      const computedStyle = window.getComputedStyle(element);
      const gap = parseFloat(computedStyle.columnGap || computedStyle.gap) || LANGUAGE_QUEUE_TAG_GAP_PX;
      const chipWidths = prioritizedReadinessChips.map(chip => estimateLanguageQueueTagWidth(chip.label));
      const total = chipWidths.length;

      for (let count = total; count >= 0; count -= 1) {
        const hiddenCount = total - count;
        const widths = chipWidths.slice(0, count);
        if (hiddenCount > 0) {
          widths.push(estimateLanguageQueueTagWidth(`+${hiddenCount}`));
        }
        if (canFitLanguageQueueTags(widths, availableWidth, gap)) {
          setVisibleChipCount(count);
          return;
        }
      }

      setVisibleChipCount(0);
    };

    updateVisibleChipCount();

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(updateVisibleChipCount);
      observer.observe(element);
      return () => observer.disconnect();
    }

    window.addEventListener('resize', updateVisibleChipCount);
    return () => window.removeEventListener('resize', updateVisibleChipCount);
  }, [bucket, prioritizedReadinessChips]);

  const visibleChips = bucket === 'not_started' ? [] : prioritizedReadinessChips.slice(0, visibleChipCount);
  const hiddenChipCount = bucket === 'not_started' ? 0 : Math.max(0, readinessChips.length - visibleChips.length);
  useLayoutEffect(() => {
    const element = tagsRef.current;
    if (!element || visibleChipCount <= 0 || element.clientWidth <= 0) {
      return;
    }
    const rows = new Set(
      Array.from(element.children).map(child => Math.round((child as HTMLElement).offsetTop)),
    );
    if (rows.size > LANGUAGE_QUEUE_TAG_MAX_ROWS) {
      setVisibleChipCount(count => Math.max(0, count - 1));
    }
  }, [visibleChipCount, hiddenChipCount]);

  const languageInitials = model.language.slice(0, 2).toUpperCase();

  return (
    <div className={`dashboard-language-queue-row dashboard-language-queue-row--${bucket}`}>
      <div className="dashboard-language-queue-cover">
        {model.coverUrl ? (
          <img src={model.coverUrl} alt={`${model.language} cover`} />
        ) : (
          <span>{languageInitials}</span>
        )}
      </div>
      <div className="dashboard-language-queue-main">
        <div className="dashboard-language-queue-heading">
          <div className="dashboard-language-queue-title">
            <h4>{model.language}</h4>
            <span>{getLanguageNextActionText(model)}</span>
          </div>
          <Button
            type={bucket === 'in_progress' ? 'primary' : 'default'}
            size="small"
            className="dashboard-language-queue-open"
            onClick={model.onOpenWorkspace}
          >
            Open
          </Button>
        </div>
        <div className="dashboard-language-queue-progress">
          {progressRows.map(row => (
            <span key={row.label} className={`dashboard-language-queue-metric dashboard-language-queue-metric--${row.tone}`}>
              {row.label}: <strong>{row.value}</strong>
            </span>
          ))}
        </div>
      </div>
      {visibleChips.length || hiddenChipCount ? (
        <div ref={tagsRef} className="dashboard-language-queue-tags" aria-label={`${model.language} readiness`}>
          {visibleChips.map(chip => {
            const className = `dashboard-readiness-chip dashboard-language-readiness-chip dashboard-language-queue-tag dashboard-readiness-chip--${chip.tone}${chip.onClick ? ' dashboard-language-readiness-chip--clickable' : ''}`;
            if (chip.onClick) {
              return (
                <button
                  key={chip.label}
                  type="button"
                  className={className}
                  onClick={chip.onClick}
                  aria-label={`${model.language}: ${chip.label}`}
                >
                  {chip.label}
                </button>
              );
            }
            return (
              <span key={chip.label} className={className}>
                {chip.label}
              </span>
            );
          })}
          {hiddenChipCount ? (
            <Tooltip title={prioritizedReadinessChips.slice(visibleChips.length).map(chip => chip.label).join(', ')}>
              <span className="dashboard-language-queue-overflow">+{hiddenChipCount}</span>
            </Tooltip>
          ) : null}
        </div>
      ) : null}
      {model.pendingProofreadReviewCount > 0 && model.onApplyPending ? (
        <div className="dashboard-language-queue-actions">
          <Button
            type="default"
            size="small"
            onClick={model.onApplyPending}
            loading={model.isApplyingPending}
          >
            Apply pending
          </Button>
        </div>
      ) : null}
    </div>
  );
};

const DashboardUtilityActions: React.FC<{
  title?: string;
  helperText?: string;
  actions: DashboardUtilityAction[];
  variant?: 'section' | 'toolbar';
}> = ({ title = 'More actions', helperText = 'Utilities and less common actions are grouped here so the main workflow stays focused.', actions, variant = 'section' }) => {
  const visibleActions = actions.filter(Boolean);
  if (!visibleActions.length) {
    return null;
  }
  const renderActions = () => (
    <Space wrap size={8}>
      {visibleActions.map(action => {
        const button = (
          <Button
            key={action.key}
            type="default"
            size="small"
            icon={action.icon}
            disabled={action.disabled}
            onClick={action.onClick}
            data-tour-id={action.tourId}
          >
            {action.label}
          </Button>
        );
        if (action.items?.length) {
          return (
            <Dropdown
              key={action.key}
              menu={{
                items: action.items.map(item => ({ key: item.key, label: item.label })),
                onClick: ({ key }) => action.onItemClick?.(String(key)),
              }}
              trigger={['click']}
            >
              {button}
            </Dropdown>
          );
        }
        if (action.disabledReason) {
          return <Tooltip key={action.key} title={action.disabledReason}>{button}</Tooltip>;
        }
        return button;
      })}
    </Space>
  );

  if (variant === 'toolbar') {
    return (
      <section
        className="dashboard-utility-actions dashboard-utility-actions--toolbar"
        aria-label={title}
        data-tour-id="dashboard-quick-links"
      >
        <span className="dashboard-utility-actions-label">{title}:</span>
        {renderActions()}
      </section>
    );
  }

  return (
    <section className="dashboard-utility-actions book-detail-secondary-surface">
      <div className="dashboard-section-heading book-detail-section-heading">
        <h3>{title}</h3>
        <p>{helperText}</p>
      </div>
      {renderActions()}
    </section>
  );
};

const DashboardStatusPill: React.FC<{
  tone: DashboardStatusTone;
  children: React.ReactNode;
}> = ({ tone, children }) => (
  <span className={`dashboard-status-pill dashboard-status-pill--${tone}`}>
    {children}
  </span>
);

const LANGUAGE_STEP_MODELS = [
  {
    key: 'glossary',
    readyLabel: 'Glossary ready',
    nextLabel: 'Glossary suggested',
    attentionLabel: 'Glossary suggested',
    mutedLabel: 'Glossary not run',
  },
  {
    key: 'translate',
    readyLabel: 'Translated',
    nextLabel: 'Translate next',
    attentionLabel: 'Translation needed',
    mutedLabel: 'Not translated',
  },
  {
    key: 'proofread',
    readyLabel: 'Proofread',
    nextLabel: 'Proofread next',
    attentionLabel: 'Proofread suggested',
    mutedLabel: 'Not proofread',
  },
  {
    key: 'metadata',
    readyLabel: 'Metadata ready',
    nextLabel: 'Metadata suggested',
    attentionLabel: 'Metadata suggested',
    mutedLabel: 'Metadata not ready',
  },
  {
    key: 'keywords',
    readyLabel: 'Keywords ready',
    nextLabel: 'Keywords suggested',
    attentionLabel: 'Keywords suggested',
    mutedLabel: 'Keywords not set',
  },
  {
    key: 'cover',
    readyLabel: 'Cover ready',
    nextLabel: 'Cover suggested',
    attentionLabel: 'Cover suggested',
    mutedLabel: 'Cover not created',
  },
] as const;

const LanguageStepIndicator: React.FC<{
  workstreams: DashboardWorkstream[];
  recommendedStepKey?: string;
  translatedCount: number;
  totalChapters: number;
  proofreadCount: number;
  isUsingSourceCover?: boolean;
}> = ({ workstreams, recommendedStepKey, translatedCount, totalChapters, proofreadCount, isUsingSourceCover }) => {
  const rowByKey = new Map<string, DashboardWorkstreamRow>();
  const rows: DashboardWorkstreamRow[] = [];
  const safeTotal = Math.max(0, totalChapters || 0);
  const safeTranslated = Math.min(Math.max(0, translatedCount || 0), safeTotal);
  const safeProofread = Math.min(Math.max(0, proofreadCount || 0), safeTotal);
  workstreams.forEach(workstream => {
    workstream.rows.forEach(row => {
      rowByKey.set(row.key, row);
      rows.push(row);
    });
  });
  const stepModels = LANGUAGE_STEP_MODELS.map((stepConfig, index) => {
    const stepKey = stepConfig.key;
    const row = rowByKey.get(stepKey) || rows.find(candidate => candidate.key.endsWith(`-${stepKey}`));
    const complete = row?.statusTone === 'ready';
    const attention = row?.statusTone === 'attention' || row?.statusTone === 'blocked';
    const current = Boolean(row?.key && recommendedStepKey === row.key);
    const chipTone = complete ? 'ready' : current ? 'attention' : attention ? 'needed' : 'muted';
    const label = complete
      ? stepConfig.readyLabel
      : current
        ? stepConfig.nextLabel
        : attention
          ? stepConfig.attentionLabel
          : stepConfig.mutedLabel;
    return {
      label,
      key: row?.key || stepKey,
      complete,
      attention,
      current,
      chipTone,
      icon: complete ? <CheckOutlined /> : current || attention ? <ExclamationCircleOutlined /> : index + 1,
      onClick: row?.onAction,
      disabled: row?.disabled || !row?.onAction,
    };
  });
  const readyCount = stepModels.filter(step => step.complete).length;
  return (
    <div className="dashboard-readiness-strip dashboard-workflow-readiness-strip" aria-label="Language workflow steps">
      <div className="dashboard-workflow-summary">
        <div className="dashboard-readiness-strip-header">
          <span>Workflow progress</span>
          <strong>{readyCount}/{stepModels.length} ready</strong>
        </div>
        <div className="dashboard-workflow-metrics" aria-label="Chapter and cover progress">
          <span className="dashboard-workflow-metric">
            <strong>{safeTranslated}/{safeTotal}</strong>
            <span>translated</span>
          </span>
          <span className="dashboard-workflow-metric">
            <strong>{safeProofread}/{safeTotal}</strong>
            <span>proofread</span>
          </span>
          <span className="dashboard-workflow-metric">
            <strong>{isUsingSourceCover ? 'Original' : 'Localized'}</strong>
            <span>cover</span>
          </span>
        </div>
        <div className="dashboard-step-indicator">
          {stepModels.map(step => (
            <button
              type="button"
              key={step.key}
              className={`dashboard-readiness-chip dashboard-step-indicator-item dashboard-workflow-step-chip dashboard-readiness-chip--${step.chipTone} ${step.complete ? 'is-complete' : step.current ? 'is-current' : step.attention ? 'is-attention' : 'is-muted'}`}
              onClick={step.onClick}
              disabled={step.disabled}
              aria-label={`${step.label}${step.complete ? ': ready' : step.current ? ': next' : step.attention ? ': needs attention' : ''}`}
            >
              <span aria-hidden="true">{step.icon}</span>
              <strong>{step.label}</strong>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

const BookOverviewDashboard: React.FC<{
  completenessItems: SourceReadinessItem[];
  languageCards: LanguageStatusCardViewModel[];
  pendingProofreadNotice?: React.ReactNode;
  recentExports: RecentExportEntry[];
  onRefreshProgress?: () => void;
  isRefreshingProgress: boolean;
  showFullLanguageFlags?: boolean;
  utilityActions: DashboardUtilityAction[];
  renderExportCard: (entry: RecentExportEntry, compact?: boolean) => React.ReactNode;
  book?: Book;
  bookLanguage?: string;
  onOpenOriginalCoverGeneration?: () => void;
  onOpenOriginalCoverHistory?: () => void;
  onOriginalCoverUpload?: (event: React.ChangeEvent<HTMLInputElement>) => void;
}> = ({
  completenessItems,
  languageCards,
  pendingProofreadNotice,
  recentExports,
  onRefreshProgress,
  isRefreshingProgress,
  showFullLanguageFlags,
  utilityActions,
  renderExportCard,
  book,
  bookLanguage,
  onOpenOriginalCoverGeneration,
  onOpenOriginalCoverHistory,
  onOriginalCoverUpload,
}) => {
  const groupedLanguageCards = groupLanguageCardsByBucket(languageCards);
  const visibleGroups = groupedLanguageCards.filter(group => group.cards.length > 0);
  const bucketCounts = buildDashboardBucketCounts(groupedLanguageCards);
  const inProgressGroup = groupedLanguageCards.find(group => group.key === 'in_progress');
  const notStartedGroup = groupedLanguageCards.find(group => group.key === 'not_started');
  const completedGroups = groupedLanguageCards.filter(group => group.key === 'complete' && group.cards.length > 0);
  const [showAllActiveLanguageWorkspaces, setShowAllActiveLanguageWorkspaces] = useState(false);
  const [isQuickLinksExpanded, setIsQuickLinksExpanded] = useState(false);
  const [isRecentExportsExpanded, setIsRecentExportsExpanded] = useState(false);
  const activeLanguageWorkspaceVisibleLimit = useActiveLanguageWorkspaceVisibleLimit();
  const activeLanguageCards = inProgressGroup?.cards ?? [];
  const visibleActiveLanguageCards = showAllActiveLanguageWorkspaces
    ? activeLanguageCards
    : activeLanguageCards.slice(0, activeLanguageWorkspaceVisibleLimit);
  const hiddenActiveLanguageCount = Math.max(0, activeLanguageCards.length - visibleActiveLanguageCards.length);
  const activeLanguageOverflowCount = Math.max(0, activeLanguageCards.length - activeLanguageWorkspaceVisibleLimit);
  const languageProgressMap = useMemo<TranslationProgressMap>(() => {
    return languageCards.reduce<TranslationProgressMap>((acc, card) => {
      const total = Math.max(0, card.totalChapters || 0);
      const translated = Math.max(0, card.translatedCount || 0);
      const percentage = total > 0 ? Math.min(100, Math.round((translated / total) * 1000) / 10) : 0;
      acc[card.language] = {
        status: total > 0 && translated >= total ? 'complete' : translated > 0 ? 'in_progress' : 'not_started',
        percentage,
        translated_chapters: translated,
        total_chapters: total,
      };
      return acc;
    }, {});
  }, [languageCards]);
  const [expandedLanguageBuckets, setExpandedLanguageBuckets] = useState<Set<LanguageOverviewBucket>>(() => new Set());
  const sectionRefs = useRef<Partial<Record<LanguageOverviewBucket, HTMLElement | null>>>({});
  const sourceReadyCount = completenessItems.filter(item => item.done).length;
  const editMetadataAction = utilityActions.find(action => action.key === 'edit-metadata');
  const addLanguageAction = utilityActions.find(action => action.key === 'add-language');
  const quickActionKeys = new Set(['edit-metadata', 'translate-book', 'proofread-book', 'upload', 'audiobook', 'export', 'support']);
  const lowerQuickActions = utilityActions.filter(action => quickActionKeys.has(action.key));
  const bookTitle = book?.title?.trim() || 'Untitled book';
  const bookAuthor = book?.author?.trim() || 'Author not set';
  const bookCoverUrl = book?.coverUrl?.trim() || '';
  const bookInitial = bookTitle.trim().charAt(0).toUpperCase() || 'B';
  const sourceLanguageLabel = bookLanguage?.trim() || 'Source';
  const sourceChapterCount = Array.isArray(book?.chapters) ? book?.chapters.length : 0;
  const [isCoverPreviewVisible, setIsCoverPreviewVisible] = useState(false);
  const coverUploadInputRef = useRef<HTMLInputElement | null>(null);
  const openCoverUploadPicker = () => {
    if (!onOriginalCoverUpload) return;
    coverUploadInputRef.current?.click();
  };
  const handleCoverDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (!onOriginalCoverUpload || !event.dataTransfer.files.length) return;
    const uploadTarget = {
      files: event.dataTransfer.files,
      value: '',
    } as HTMLInputElement;
    onOriginalCoverUpload({
      target: uploadTarget,
      currentTarget: uploadTarget,
    } as React.ChangeEvent<HTMLInputElement>);
  };
  const toggleLanguageBucket = (bucket: LanguageOverviewBucket) => {
    setExpandedLanguageBuckets(previous => {
      const next = new Set(previous);
      if (next.has(bucket)) {
        next.delete(bucket);
      } else {
        next.add(bucket);
      }
      return next;
    });
  };
  const scrollToLanguageSection = (bucket: LanguageOverviewBucket) => {
    const section = sectionRefs.current[bucket];
    if (!section) {
      return;
    }
    section.scrollIntoView({ block: 'start', behavior: 'smooth' });
    section.focus({ preventScroll: true });
  };
  const openMetadataSection = (targetId: string) => {
    editMetadataAction?.onClick?.();
    window.setTimeout(() => {
      const target = document.querySelector(`[data-tour-id="${targetId}"]`) as HTMLElement | null;
      target?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }, 80);
  };
  const languageProgressContent = (
    <div
      className="dashboard-language-summary dashboard-language-summary--command"
      aria-label="Language progress"
      data-tour-id="dashboard-progress"
    >
      <div className="dashboard-readiness-strip-header">
        <span>Language progress</span>
        <span className="dashboard-language-progress-header-actions">
          <strong>{formatDashboardCount(languageCards.length)} total</strong>
          {onRefreshProgress ? (
            <Tooltip title="Refresh progress">
              <Button
                type="text"
                size="small"
                icon={<ReloadOutlined spin={isRefreshingProgress} />}
                onClick={onRefreshProgress}
                disabled={isRefreshingProgress}
                aria-label="Refresh progress"
                className="dashboard-progress-refresh-button"
              />
            </Tooltip>
          ) : null}
        </span>
      </div>
      {showFullLanguageFlags ? (
        <LanguageProgressChips
          className="dashboard-language-progress-flags"
          progress={languageProgressMap}
          languages={languageCards.map(card => card.language)}
          balanceWrappedRows
          onSelectLanguage={(language) => {
            const card = languageCards.find(item => languagesMatch(item.language, language));
            card?.onOpenWorkspace();
          }}
        />
      ) : (
        <div className="dashboard-language-counts">
          {bucketCounts.map(bucket => (
            <button
              key={bucket.key}
              type="button"
              className={`dashboard-language-count dashboard-language-count--${bucket.key}`}
              aria-label={bucket.count ? `Show ${bucket.title} languages` : `No ${bucket.title} languages`}
              aria-controls={`dashboard-language-group-${bucket.key}`}
              disabled={bucket.count === 0}
              onClick={() => scrollToLanguageSection(bucket.key)}
            >
              <strong>{formatDashboardCount(bucket.count)}</strong>
              <span>{bucket.title}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
  return (
    <div className="dashboard-mode-shell dashboard-mode-shell--overview" data-testid="tutorial:dashboard:overview">
      <section className="dashboard-dominant-surface book-detail-primary-surface book-detail-workbench-stack">
        <div className="dashboard-guided-command book-detail-workbench-section">
          <div className="dashboard-book-identity" aria-label="Book overview" data-testid="tutorial:dashboard:book-summary">
            <div className="dashboard-cover-command" data-testid="tutorial:dashboard:cover">
              <div
                className={`dashboard-book-cover${!bookCoverUrl && onOriginalCoverUpload ? ' metadata-cover-upload dashboard-book-cover--uploadable' : ''}`}
                role={!bookCoverUrl && onOriginalCoverUpload ? 'button' : undefined}
                tabIndex={!bookCoverUrl && onOriginalCoverUpload ? 0 : undefined}
                onClick={!bookCoverUrl && onOriginalCoverUpload ? openCoverUploadPicker : undefined}
                onKeyDown={!bookCoverUrl && onOriginalCoverUpload ? (event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    openCoverUploadPicker();
                  }
                } : undefined}
                onDragOver={!bookCoverUrl && onOriginalCoverUpload ? (event) => {
                  event.preventDefault();
                  event.stopPropagation();
                } : undefined}
                onDrop={!bookCoverUrl && onOriginalCoverUpload ? handleCoverDrop : undefined}
              >
                {bookCoverUrl ? (
                  <img src={bookCoverUrl} alt={`${bookTitle} cover`} loading="lazy" />
                ) : onOriginalCoverUpload ? (
                  <>
                    <FileImageOutlined className="metadata-cover-upload-icon" />
                    <span>Click or drag cover image</span>
                  </>
                ) : (
                  <span>{bookInitial}</span>
                )}
              </div>
              {onOriginalCoverUpload ? (
                <input
                  ref={coverUploadInputRef}
                  type="file"
                  onChange={onOriginalCoverUpload}
                  accept="image/*"
                  style={{ display: 'none' }}
                  aria-hidden="true"
                  tabIndex={-1}
                />
              ) : null}
              <div className="dashboard-cover-actions" aria-label="Cover actions">
                {bookCoverUrl ? (
                  <Tooltip title="View cover">
                    <Button
                      size="small"
                      icon={<EyeOutlined />}
                      className="dashboard-cover-action-button"
                      onClick={() => setIsCoverPreviewVisible(true)}
                      aria-label="View cover"
                    >
                      View cover
                    </Button>
                  </Tooltip>
                ) : null}
                <Tooltip title="Generate cover">
                  <Button
                    size="small"
                    icon={<FileImageOutlined />}
                    className="dashboard-cover-action-button"
                    onClick={onOpenOriginalCoverGeneration}
                    disabled={!onOpenOriginalCoverGeneration}
                    aria-label="Generate cover"
                  >
                    Generate cover
                  </Button>
                </Tooltip>
                <Tooltip title="Cover history">
                  <Button
                    size="small"
                    icon={<HistoryOutlined />}
                    className="dashboard-cover-action-button"
                    onClick={onOpenOriginalCoverHistory}
                    disabled={!onOpenOriginalCoverHistory}
                    aria-label="Cover history"
                  >
                    Cover history
                  </Button>
                </Tooltip>
              </div>
            </div>
            <div className="dashboard-book-identity-copy dashboard-cover-copy">
              <h2>{bookTitle}</h2>
              <p>{bookAuthor}</p>
              <div className="metadata-original-source-stats dashboard-cover-facts" aria-label="Source workspace facts">
                <span><strong>{sourceLanguageLabel}</strong> source</span>
                <span><strong>{sourceChapterCount}</strong> {sourceChapterCount === 1 ? 'chapter' : 'chapters'}</span>
              </div>
            </div>
          </div>
          <div className="dashboard-command-status-stack" aria-label="Book dashboard summary" data-testid="tutorial:dashboard:status-summary">
            <div className="dashboard-source-readiness dashboard-source-readiness--command" data-testid="tutorial:dashboard:source-readiness">
              <div className="dashboard-readiness-strip-header">
                <span>Source readiness</span>
                <strong>{sourceReadyCount}/{completenessItems.length} ready</strong>
              </div>
              <div className="dashboard-completeness-row">
                {completenessItems.map(item => (
                  <button
                    key={item.label}
                    type="button"
                    className={`dashboard-readiness-chip dashboard-readiness-chip--${item.done ? 'ready' : 'needed'}`}
                    onClick={() => openMetadataSection(item.targetId)}
                  >
                    {item.label}: {item.done ? 'Ready' : 'Missing'}
                  </button>
                ))}
              </div>
            </div>
            <div data-testid="tutorial:dashboard:language-progress">
              {languageProgressContent}
            </div>
          </div>
        </div>
        {pendingProofreadNotice ? (
          <div className="dashboard-original-proofread-notice">
            {pendingProofreadNotice}
          </div>
        ) : null}
        <div
          className="dashboard-lower-grid book-detail-workbench-section book-detail-workbench-section--divided"
          data-testid="tutorial:dashboard:lower-grid"
        >
          <section className="dashboard-lower-panel dashboard-lower-panel--quick-links" data-tour-id="dashboard-quick-links" data-testid="tutorial:dashboard:quick-links">
            <div className="dashboard-lower-panel-heading dashboard-lower-panel-heading--collapsible">
              <div>
                <h3>Quick Links</h3>
                <p>Book-level actions and setup tools.</p>
              </div>
              <Button
                type="text"
                size="small"
                onClick={() => setIsQuickLinksExpanded(previous => !previous)}
                aria-expanded={isQuickLinksExpanded}
                aria-controls="dashboard-quick-links-content"
                className="dashboard-secondary-panel-toggle"
              >
                {isQuickLinksExpanded ? 'Hide' : 'Show'}
              </Button>
            </div>
            {isQuickLinksExpanded ? <div id="dashboard-quick-links-content" className="dashboard-quick-link-list">
              {lowerQuickActions.map(action => {
                const button = (
                  <Button
                    key={action.key}
                    type="default"
                    size="small"
                    icon={action.icon}
                    disabled={action.disabled}
                    onClick={action.onClick}
                    className="dashboard-quick-link-button"
                    data-tour-id={action.tourId}
                    data-testid={action.tourId ? `tutorial:dashboard:${action.tourId}` : undefined}
                  >
                    {action.label}
                  </Button>
                );
                if (action.items?.length) {
                  return (
                    <Dropdown
                      key={action.key}
                      menu={{
                        items: action.items.map(item => ({ key: item.key, label: item.label })),
                        onClick: ({ key }) => action.onItemClick?.(String(key)),
                      }}
                      trigger={['click']}
                    >
                      {button}
                    </Dropdown>
                  );
                }
                if (action.disabledReason) {
                  return <Tooltip key={action.key} title={action.disabledReason}>{button}</Tooltip>;
                }
                return button;
              })}
            </div> : null}
          </section>
          <section className="dashboard-lower-panel dashboard-lower-panel--exports" data-tour-id="dashboard-exports" data-testid="tutorial:dashboard:recent-exports">
            <div className="dashboard-lower-panel-heading dashboard-lower-panel-heading--collapsible">
              <div>
                <h3>Recent Exports</h3>
                <p>Latest generated files for this book.</p>
              </div>
              <Button
                type="text"
                size="small"
                onClick={() => setIsRecentExportsExpanded(previous => !previous)}
                aria-expanded={isRecentExportsExpanded}
                aria-controls="dashboard-recent-exports-content"
                className="dashboard-secondary-panel-toggle"
              >
                {isRecentExportsExpanded ? 'Hide' : 'Show'}
              </Button>
            </div>
            {isRecentExportsExpanded ? (
              <div id="dashboard-recent-exports-content" className="dashboard-secondary-panel-content">
                <Button
                  type="default"
                  size="small"
                  icon={<ExportOutlined />}
                  onClick={utilityActions.find(action => action.key === 'export')?.onClick}
                  className="dashboard-export-action-button"
                >
                  Export Book
                </Button>
                {recentExports.length ? (
                  <div className="dashboard-export-list">
                    {recentExports.slice(0, 3).map(entry => (
                      <React.Fragment key={entry.exportId || `${entry.version}-${entry.completedAt}`}>
                        {renderExportCard(entry, true)}
                      </React.Fragment>
                    ))}
                  </div>
                ) : (
                  <div className="dashboard-empty-state dashboard-empty-state--compact">No exports yet.</div>
                )}
              </div>
            ) : null}
          </section>
          <section
            id="dashboard-language-group-in_progress"
            ref={(element) => {
              sectionRefs.current.in_progress = element;
            }}
            className="dashboard-lower-panel dashboard-lower-panel--languages dashboard-language-queue"
            data-testid="tutorial:dashboard:language-workspaces"
            tabIndex={-1}
          >
            <div className="dashboard-lower-panel-heading dashboard-lower-panel-heading--inline">
              <div>
                <h3>Language Workspaces</h3>
                <p>Active languages first; quieter groups stay collapsed.</p>
              </div>
              <strong>{formatDashboardCount(languageCards.length)} total</strong>
            </div>
            {activeLanguageCards.length ? (
              <div className="dashboard-language-queue-list" id="dashboard-language-queue-active">
                {visibleActiveLanguageCards.map(model => (
                  <CompactLanguageQueueRow key={model.language} model={model} bucket="in_progress" />
                ))}
              </div>
            ) : (
              <div className="dashboard-empty-state dashboard-empty-state--compact">No active language workspaces yet.</div>
            )}
            {activeLanguageOverflowCount ? (
              <button
                type="button"
                className="dashboard-language-queue-summary dashboard-language-queue-summary--active-overflow"
                onClick={() => setShowAllActiveLanguageWorkspaces(previous => !previous)}
                aria-expanded={showAllActiveLanguageWorkspaces}
                aria-controls="dashboard-language-queue-active"
              >
                <span>
                  {showAllActiveLanguageWorkspaces
                    ? 'Showing all active languages'
                    : `${formatDashboardCount(hiddenActiveLanguageCount)} more active language${hiddenActiveLanguageCount === 1 ? '' : 's'}`}
                </span>
                <strong>{showAllActiveLanguageWorkspaces ? 'Show less' : 'Show'}</strong>
              </button>
            ) : null}
            {(notStartedGroup?.cards.length || addLanguageAction) ? (() => {
              const isExpanded = expandedLanguageBuckets.has('not_started');
              const unusedLanguageCount = notStartedGroup?.cards.length || 0;
              return (
                <section
                  id="dashboard-language-group-not_started"
                  ref={(element) => {
                    sectionRefs.current.not_started = element;
                  }}
                  className="dashboard-language-queue-bucket dashboard-language-queue-bucket--not_started"
                >
                  <button
                    type="button"
                    className="dashboard-language-queue-summary"
                    onClick={() => toggleLanguageBucket('not_started')}
                    aria-expanded={isExpanded}
                    aria-controls="dashboard-language-queue-not_started"
                  >
                    <span>
                      Add or view languages{unusedLanguageCount ? ` (${formatDashboardCount(unusedLanguageCount)} unused)` : ''}
                    </span>
                    <strong>{isExpanded ? 'Hide' : 'Show'}</strong>
                  </button>
                  {isExpanded ? (
                    <div id="dashboard-language-queue-not_started" className="dashboard-language-library">
                      {addLanguageAction ? (
                        <div className="dashboard-language-library-actions">
                          <Button
                            type="default"
                            size="small"
                            icon={addLanguageAction.icon}
                            onClick={addLanguageAction.onClick}
                          >
                            Add language
                          </Button>
                        </div>
                      ) : null}
                      {notStartedGroup?.cards.length ? (
                        <div className="dashboard-language-queue-list">
                          {notStartedGroup.cards.map(model => (
                            <CompactLanguageQueueRow key={model.language} model={model} bucket="not_started" />
                          ))}
                        </div>
                      ) : (
                        <div className="dashboard-empty-state dashboard-empty-state--compact">No unused language workspaces.</div>
                      )}
                    </div>
                  ) : null}
                </section>
              );
            })() : null}
            {completedGroups.map(group => {
              const isExpanded = expandedLanguageBuckets.has(group.key);
              return (
                <section
                  key={group.key}
                  id={`dashboard-language-group-${group.key}`}
                  ref={(element) => {
                    sectionRefs.current[group.key] = element;
                  }}
                  className={`dashboard-language-queue-bucket dashboard-language-queue-bucket--${group.key}`}
                >
                  <button
                    type="button"
                    className="dashboard-language-queue-summary"
                    onClick={() => toggleLanguageBucket(group.key)}
                    aria-expanded={isExpanded}
                    aria-controls={`dashboard-language-queue-${group.key}`}
                  >
                    <span>
                      {formatDashboardCount(group.cards.length)} {group.title.toLowerCase()} language{group.cards.length === 1 ? '' : 's'}
                    </span>
                    <strong>{isExpanded ? 'Hide' : 'Show'}</strong>
                  </button>
                  {isExpanded ? (
                    <div id={`dashboard-language-queue-${group.key}`} className="dashboard-language-queue-list">
                      {group.cards.map(model => (
                        <CompactLanguageQueueRow key={model.language} model={model} bucket={group.key} />
                      ))}
                    </div>
                  ) : null}
                </section>
              );
            })}
            {!visibleGroups.length ? (
              <div className="dashboard-empty-state dashboard-empty-state--compact">No target languages yet. Add a language to begin translation work.</div>
            ) : null}
          </section>
        </div>
      </section>
      {bookCoverUrl ? (
        <Modal
          visible={isCoverPreviewVisible}
          title={`${bookTitle} cover`}
          footer={null}
          onCancel={() => setIsCoverPreviewVisible(false)}
          centered
          width="min(760px, calc(100vw - 32px))"
          className="metadata-cover-preview-modal"
        >
          <div className="metadata-cover-preview-body">
            <div className="metadata-cover-preview-stage">
              <img src={bookCoverUrl} alt={`${bookTitle} cover preview`} className="metadata-cover-preview-large" />
            </div>
          </div>
        </Modal>
      ) : null}
    </div>
  );
};

const LanguageWorkspaceDashboard: React.FC<{
  language: string;
  bookTitle: string;
  bookAuthor: string;
  summaryText: string;
  onBack?: () => void;
  readinessLabel: string;
  readinessTone: DashboardStatusTone;
  workstreams: DashboardWorkstream[];
  recommendedStep: DashboardStep | null;
  quickActions: DashboardUtilityAction[];
  coverUrl?: string;
  isUsingSourceCover?: boolean;
  coverInitials: string;
  translatedCount: number;
  totalChapters: number;
  proofreadCount: number;
  recentExports: RecentExportEntry[];
  renderExportCard: (entry: RecentExportEntry, compact?: boolean) => React.ReactNode;
  onOpenCoverGeneration?: () => void;
  onOpenCoverHistory?: () => void;
  renderDismissControl: (stepKey: string) => React.ReactNode;
}> = ({
  language,
  bookTitle,
  bookAuthor,
  summaryText,
  onBack,
  readinessLabel,
  readinessTone,
  workstreams,
  recommendedStep,
  quickActions,
  coverUrl,
  isUsingSourceCover,
  coverInitials,
  translatedCount,
  totalChapters,
  proofreadCount,
  recentExports,
  renderExportCard,
  onOpenCoverGeneration,
  onOpenCoverHistory,
  renderDismissControl,
}) => {
  const safeTotal = Math.max(0, totalChapters || 0);
  const safeTranslated = Math.min(Math.max(0, translatedCount || 0), safeTotal);
  const safeProofread = Math.min(Math.max(0, proofreadCount || 0), safeTotal);
  const [isCoverPreviewVisible, setIsCoverPreviewVisible] = useState(false);
  const [isQuickLinksExpanded, setIsQuickLinksExpanded] = useState(false);
  const [isRecentExportsExpanded, setIsRecentExportsExpanded] = useState(false);
  const exportQuickAction = quickActions.find(action => action.key === 'export');
  const nextStepRows = workstreams.flatMap(workstream =>
    workstream.rows.map(row => ({
      ...row,
      workstreamTitle: workstream.title,
    })),
  );
  const visibleNextStepRows = nextStepRows.filter(row => !(row.statusTone === 'ready' && !row.actionLabel));
  const readyNextStepCount = nextStepRows.filter(row => row.statusTone === 'ready').length;
  const renderQuickActionButton = (action: DashboardUtilityAction, testIdOverride?: string) => {
    const button = (
      <Button
        key={action.key}
        type="default"
        size="small"
        icon={action.icon}
        disabled={action.disabled}
        onClick={action.onClick}
        className="dashboard-translated-quick-link"
        data-tour-id={action.tourId}
        data-testid={testIdOverride || (action.tourId ? `tutorial:dashboard:${action.tourId}` : undefined)}
      >
        {action.label}
      </Button>
    );
    if (action.items?.length) {
      return (
        <Dropdown
          key={action.key}
          menu={{
            items: action.items.map(item => ({ key: item.key, label: item.label })),
            onClick: ({ key }) => action.onItemClick?.(String(key)),
          }}
          trigger={['click']}
        >
          {button}
        </Dropdown>
      );
    }
    if (action.disabledReason) {
      return <Tooltip key={action.key} title={action.disabledReason}>{button}</Tooltip>;
    }
    return button;
  };
  const productionExports = (
    <div className="dashboard-production-exports">
      {recentExports.length ? (
        <div className="dashboard-production-export-list">
          {recentExports.slice(0, 2).map(entry => (
            <React.Fragment key={entry.exportId || `${entry.version}-${entry.completedAt}`}>
              {renderExportCard(entry, true)}
            </React.Fragment>
          ))}
        </div>
      ) : (
        <div className="dashboard-empty-state dashboard-empty-state--compact">No exports yet.</div>
      )}
    </div>
  );

  return (
    <div className="dashboard-mode-shell dashboard-mode-shell--language" data-testid="tutorial:dashboard:translated-overview">
      <section className="dashboard-dominant-surface book-detail-primary-surface">
        <div className="dashboard-language-command">
          <div
            className="dashboard-language-identity"
            aria-label={`${language} edition overview`}
            data-testid="tutorial:dashboard:translated-summary"
          >
            <div className="dashboard-language-identity-main">
              <div className="dashboard-cover-command dashboard-language-cover-command">
                <div className={`dashboard-language-edition-cover${isUsingSourceCover ? ' dashboard-language-edition-cover--fallback' : ''}`}>
                  {coverUrl ? (
                    <img src={coverUrl} alt={`${language} edition cover`} loading="lazy" />
                  ) : (
                    <span>{coverInitials}</span>
                  )}
                </div>
                <div className="dashboard-cover-actions dashboard-language-cover-actions" aria-label={`${language} cover actions`}>
                  {coverUrl ? (
                    <Tooltip title="View cover">
                      <Button
                        size="small"
                        icon={<EyeOutlined />}
                        className="dashboard-cover-action-button"
                        onClick={() => setIsCoverPreviewVisible(true)}
                        aria-label="View cover"
                      >
                        View cover
                      </Button>
                    </Tooltip>
                  ) : null}
                  <Tooltip title="Translate cover">
                    <Button
                      size="small"
                      icon={<TranslationOutlined />}
                      className="dashboard-cover-action-button"
                      onClick={onOpenCoverGeneration}
                      disabled={!onOpenCoverGeneration}
                      aria-label="Translate cover"
                    >
                      Translate cover
                    </Button>
                  </Tooltip>
                  <Tooltip title="Cover history">
                    <Button
                      size="small"
                      icon={<HistoryOutlined />}
                      className="dashboard-cover-action-button"
                      onClick={onOpenCoverHistory}
                      disabled={!onOpenCoverHistory}
                      aria-label="Cover history"
                    >
                      Cover history
                    </Button>
                  </Tooltip>
                </div>
              </div>
              <div className="dashboard-language-command-copy">
                {onBack ? (
                  <Button
                    type="link"
                    size="small"
                    icon={<ArrowLeftOutlined />}
                    onClick={onBack}
                    className="dashboard-language-back-action"
                  >
                    Book dashboard
                  </Button>
                ) : null}
                <div className="book-detail-eyebrow dashboard-section-kicker">{language} edition</div>
                <div className="dashboard-language-title-row">
                  <h2>{bookTitle}</h2>
                </div>
                <p className="dashboard-cover-author">{bookAuthor}</p>
                {summaryText ? <p className="book-detail-helper-text">{summaryText}</p> : null}
              </div>
            </div>
          </div>
          <div data-testid="tutorial:dashboard:translated-workflow-progress">
            <LanguageStepIndicator
              workstreams={workstreams}
              recommendedStepKey={recommendedStep?.key}
              translatedCount={safeTranslated}
              totalChapters={safeTotal}
              proofreadCount={safeProofread}
              isUsingSourceCover={isUsingSourceCover}
            />
          </div>
        </div>
        <div className="dashboard-translated-lower-grid" aria-label={`${language} edition readiness`} data-tour-id="dashboard-progress" data-testid="tutorial:dashboard:translated-progress">
          <section className="dashboard-translated-quick-links" data-testid="tutorial:dashboard:translated-quick-links">
            <div className="dashboard-lower-panel-heading dashboard-lower-panel-heading--collapsible">
              <div>
                <h3>Quick links</h3>
                <p>Language tools and book-level actions.</p>
              </div>
              <Button
                type="text"
                size="small"
                onClick={() => setIsQuickLinksExpanded(previous => !previous)}
                aria-expanded={isQuickLinksExpanded}
                aria-controls="dashboard-language-quick-links-content"
                className="dashboard-secondary-panel-toggle"
              >
                {isQuickLinksExpanded ? 'Hide' : 'Show'}
              </Button>
            </div>
            {isQuickLinksExpanded ? <div id="dashboard-language-quick-links-content" className="dashboard-translated-quick-link-list">
              {quickActions.map(action => renderQuickActionButton(
                action,
                action.key === 'export' ? 'tutorial:dashboard:translated-export-quick-link' : undefined,
              ))}
            </div> : null}
          </section>
          <section className="dashboard-translated-exports" data-testid="tutorial:dashboard:translated-exports">
            <div className="dashboard-lower-panel-heading dashboard-lower-panel-heading--collapsible">
              <div>
                <h3>Recent exports</h3>
                <p>Latest generated files for {language}.</p>
              </div>
              <Button
                type="text"
                size="small"
                onClick={() => setIsRecentExportsExpanded(previous => !previous)}
                aria-expanded={isRecentExportsExpanded}
                aria-controls="dashboard-language-recent-exports-content"
                className="dashboard-secondary-panel-toggle"
              >
                {isRecentExportsExpanded ? 'Hide' : 'Show'}
              </Button>
            </div>
            {isRecentExportsExpanded ? (
              <div id="dashboard-language-recent-exports-content" className="dashboard-secondary-panel-content">
                {exportQuickAction ? renderQuickActionButton(exportQuickAction, 'tutorial:dashboard:translated-export-book') : null}
                {productionExports}
              </div>
            ) : null}
          </section>
          <section className="dashboard-translated-next-steps" data-testid="tutorial:dashboard:translated-next-steps">
            <div className="dashboard-lower-panel-heading dashboard-lower-panel-heading--inline">
              <div>
                <h3>Next steps</h3>
                <p>Finish the active publishing tasks for {language}.</p>
              </div>
              <strong>{readyNextStepCount}/{nextStepRows.length} ready</strong>
            </div>
            {visibleNextStepRows.length ? (
              <div className="dashboard-next-step-grid">
                {visibleNextStepRows.map(row => (
                  <div
                    key={row.key}
                    className={`dashboard-next-step-card${row.status === 'Next' ? ' dashboard-next-step-card--next' : ''}${row.statusTone === 'ready' ? ' dashboard-next-step-card--ready' : ''}${row.statusTone === 'blocked' ? ' dashboard-next-step-card--blocked' : ''}${row.actionLabel && row.onAction ? ' dashboard-next-step-card--has-action' : ''}`}
                  >
                    <div className="dashboard-next-step-card-header">
                      <div className="dashboard-next-step-card-title">
                        <strong>{row.title}</strong>
                        <DashboardStatusPill tone={row.statusTone}>{row.status}</DashboardStatusPill>
                      </div>
                      {row.dismissKey ? (
                        <div className="dashboard-next-step-card-dismiss">
                          {renderDismissControl(row.dismissKey)}
                        </div>
                      ) : null}
                    </div>
                    <div className="dashboard-next-step-card-copy">
                      {row.detail ? <p>{row.detail}</p> : null}
                      {renderProgressBar(row.progressPercent)}
                    </div>
                    {row.actionLabel && row.onAction ? (
                      <div className="dashboard-next-step-card-actions">
                        <Button
                          type={row.statusTone === 'attention' || row.statusTone === 'blocked' ? 'primary' : 'default'}
                          size="small"
                          onClick={row.onAction}
                          disabled={row.disabled}
                          data-testid={row.key === 'export-book' ? 'tutorial:dashboard:translated-export-next-step' : undefined}
                        >
                          {row.actionLabel}
                        </Button>
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            ) : (
              <div className="dashboard-empty-state dashboard-empty-state--compact">All current next steps are complete.</div>
            )}
          </section>
        </div>
      </section>
      {coverUrl ? (
        <Modal
          visible={isCoverPreviewVisible}
          title={`${language} cover`}
          footer={null}
          onCancel={() => setIsCoverPreviewVisible(false)}
          centered
          width="min(760px, calc(100vw - 32px))"
          className="metadata-cover-preview-modal"
        >
          <div className="metadata-cover-preview-body">
            <div className="metadata-cover-preview-stage">
              <img src={coverUrl} alt={`${language} cover preview`} className="metadata-cover-preview-large" />
            </div>
          </div>
        </Modal>
      ) : null}
    </div>
  );
};

export const countProofreadChaptersForLanguage = ({
  book,
  language,
  bookLanguage,
  translationPairs,
}: {
  book?: Book;
  language: string;
  bookLanguage?: string;
  translationPairs?: string[];
}): number => {
  let sourceLang = bookLanguage || '';
  let targetLanguageForKey = language;
  const pair = (translationPairs || []).find(item => languagesMatch(splitLanguagePair(item || '').targetLang, language));
  if (pair) {
    const { sourceLang: resolvedSourceLang, targetLang: resolvedTargetLang } = splitLanguagePair(pair);
    sourceLang = resolvedSourceLang;
    targetLanguageForKey = resolvedTargetLang;
  }
  if (!sourceLang) {
    return 0;
  }
  const key = `translated_chapters_${sourceLang}_${targetLanguageForKey}`;
  const translatedChapters = (book as any)?.[key] || [];
  return translatedChapters.filter((chapter: any) => hasCompletedProofreadProgressForRow(chapter)).length;
};

export const countPendingProofreadReviewsForLanguage = ({
  book,
  language,
  bookLanguage,
  translationPairs,
}: {
  book?: Book;
  language: string;
  bookLanguage?: string;
  translationPairs?: string[];
}): number => {
  if (!language || language.toLowerCase() === 'original') {
    return ((book as any)?.chapters || []).filter((chapter: any) => hasPendingProofreadReviewForRow(chapter)).length;
  }
  let sourceLang = bookLanguage || '';
  let targetLanguageForKey = language;
  const pair = (translationPairs || []).find(item => languagesMatch(splitLanguagePair(item || '').targetLang, language));
  if (pair) {
    const { sourceLang: resolvedSourceLang, targetLang: resolvedTargetLang } = splitLanguagePair(pair);
    sourceLang = resolvedSourceLang;
    targetLanguageForKey = resolvedTargetLang;
  }
  if (!sourceLang) {
    return 0;
  }
  const key = `translated_chapters_${sourceLang}_${targetLanguageForKey}`;
  const translatedChapters = (book as any)?.[key] || [];
  return translatedChapters.filter((chapter: any) => hasPendingProofreadReviewForRow(chapter)).length;
};

const BookDashboardTab: React.FC<BookDashboardTabProps> = ({
  showTranslationProgress,
  translationProgress,
  translationLanguages,
  onSelectTranslationLanguage,
  onEditMetadata,
  onCreateAudiobook,
  onExportBook,
  onUploadChapters,
  onTranslateBook,
  onProofreadBook,
  onAddLanguage,
  selectedLanguage,
  recentExports,
  onDownloadRecentExport,
  onRefreshProgress,
  isRefreshingProgress = false,
  userId,
  bookId,
  translationTrackerData,
  onOpenMetadataForLanguage,
  onOpenMetadataTranslateForLanguage,
  onOpenKeywordSuggestionsForLanguage,
  onOpenCoverTranslationForLanguage,
  onOpenCoverHistoryForLanguage,
  onOpenProofreadForLanguage,
  onApplyPendingProofreadReviewsForLanguage,
  applyingPendingProofreadLanguage,
  onOpenGlossaryScanForLanguage,
  onReturnToOriginalDashboard,
  onSelectDashboardLanguage,
  availableLanguages,
  bookLanguage,
  translationPairs,
  keywordTranslations,
  neverTranslateWords,
  alwaysTranslateWords,
  accountNeverTranslateWords,
  accountAlwaysTranslateWords,
  seriesNeverTranslateWords,
  seriesAlwaysTranslateWords,
  languageCovers,
  book,
  onOpenRelationshipAnalysis,
  onOpenCharactersTab,
  isUploadDisabled,
  uploadDisabledReason,
  onOriginalCoverUpload,
  hasTitle,
  hasAuthor,
  hasBlurb,
  hasGenres,
  hasKeywords,
  hasCover,
  frenchDialogueLegacyEligibilityByPair,
  onFetchFrenchDialogueLegacyEligibility,
  onRunFrenchDialogueLegacyCorrection,
  onDashboardOverflowActionsChange,
}) => {
  const navigate = useNavigate();
  const [dismissedSteps, setDismissedSteps] = useState<Set<string>>(new Set());
  const [downloadingExportId, setDownloadingExportId] = useState<string | null>(null);
  const [confirmStepKey, setConfirmStepKey] = useState<string | null>(null);
  const [latestTermScanByLang, setLatestTermScanByLang] = useState<Record<string, any>>({});
  const termScanFetchIdRef = useRef(0);
  const normalizedTranslationProgress = useMemo(
    () => canonicalizeTranslationProgressMap(translationProgress),
    [translationProgress],
  );
  const languageOrderRef = useRef<Map<string, number>>(new Map());
  const nextLanguageOrderRef = useRef(0);

  const selectedLanguageKey = useMemo(() => {
    if (!selectedLanguage || selectedLanguage === 'original') {
      return null;
    }
    const { targetLang } = splitLanguagePair(selectedLanguage);
    if (targetLang) {
      return normalizeLangKey(targetLang) || null;
    }
    return normalizeLangKey(selectedLanguage) || null;
  }, [selectedLanguage]);
  const selectedLanguagePair = useMemo(() => {
    if (!selectedLanguage || selectedLanguage === 'original') {
      return '';
    }
    const requested = selectedLanguage.trim();
    const pairCandidates = Array.from(new Set([
      ...(translationPairs || []),
      ...(((book as any)?.translation_pairs || []) as string[]),
    ].map(pair => String(pair || '').trim()).filter(Boolean)));
    const exactMatch = pairCandidates.find(pair => pair.toLowerCase() === requested.toLowerCase());
    if (exactMatch) {
      return exactMatch;
    }

    const requestedTargetKey = normalizeLangKey(requested);
    const requestedSourceKey = normalizeLangKey(bookLanguage);
    const matchingPair = pairCandidates.find(pair => {
      const { sourceLang, targetLang } = splitLanguagePair(pair);
      if (!targetLang || normalizeLangKey(targetLang) !== requestedTargetKey) {
        return false;
      }
      return !requestedSourceKey || normalizeLangKey(sourceLang) === requestedSourceKey;
    }) || pairCandidates.find(pair => {
      const { targetLang } = splitLanguagePair(pair);
      return Boolean(targetLang) && normalizeLangKey(targetLang) === requestedTargetKey;
    });
    if (matchingPair) {
      return matchingPair;
    }

    const { sourceLang, targetLang } = splitLanguagePair(requested);
    if (sourceLang && targetLang) {
      return requested;
    }
    return bookLanguage ? `${bookLanguage}-${requested}` : requested;
  }, [book, bookLanguage, selectedLanguage, translationPairs]);
  const { targetLang: selectedPairTargetLang } = useMemo(
    () => splitLanguagePair(selectedLanguagePair),
    [selectedLanguagePair],
  );
  const selectedTargetLanguage = (selectedPairTargetLang || '').trim();
  const selectedPendingProofreadReviewCount = useMemo(() => {
    if (!selectedTargetLanguage) {
      return 0;
    }
    return countPendingProofreadReviewsForLanguage({
      book,
      language: selectedTargetLanguage,
      bookLanguage,
      translationPairs,
    });
  }, [book, bookLanguage, selectedTargetLanguage, translationPairs]);
  const normalizedApplyingPendingProofreadLanguage = normalizeLangKey(applyingPendingProofreadLanguage);
  const isApplyingPendingProofreadForLanguage = (language: string) =>
    Boolean(normalizedApplyingPendingProofreadLanguage)
    && normalizeLangKey(language) === normalizedApplyingPendingProofreadLanguage;
  const selectedPendingProofreadApplyInFlight = selectedTargetLanguage
    ? isApplyingPendingProofreadForLanguage(selectedTargetLanguage)
    : false;
  const originalPendingProofreadReviewCount = useMemo(
    () => ((book as any)?.chapters || []).filter((chapter: any) => hasPendingProofreadReviewForRow(chapter)).length,
    [book],
  );
  const originalPendingProofreadApplyInFlight = isApplyingPendingProofreadForLanguage('Original');
  const selectedIsFrenchPair = normalizeLangKey(selectedTargetLanguage) === 'french';
  const selectedFrenchEligibility = selectedLanguagePair
    ? frenchDialogueLegacyEligibilityByPair?.[selectedLanguagePair]
    : undefined;
  const selectedFrenchEligibilityKnown = typeof selectedFrenchEligibility?.eligible === 'boolean';

  useEffect(() => {
    if (!selectedLanguagePair || !selectedIsFrenchPair || !onFetchFrenchDialogueLegacyEligibility) {
      return;
    }
    void onFetchFrenchDialogueLegacyEligibility({
      pair: selectedLanguagePair,
      targetLanguage: selectedTargetLanguage || 'French',
      force: false,
    });
  }, [
    onFetchFrenchDialogueLegacyEligibility,
    selectedIsFrenchPair,
    selectedLanguagePair,
    selectedTargetLanguage,
  ]);

  const glossaryScanLanguages = useMemo(() => {
    const languageSet = new Set<string>();
    (availableLanguages || []).forEach(language => {
      const normalizedLanguage = canonicalizeLanguageName(language);
      if (normalizedLanguage) {
        languageSet.add(normalizedLanguage);
      }
    });
    (translationLanguages || []).forEach(language => {
      const normalizedLanguage = canonicalizeLanguageName(language);
      if (normalizedLanguage) {
        languageSet.add(normalizedLanguage);
      }
    });
    Object.keys(normalizedTranslationProgress || {}).forEach(language => {
      const normalizedLanguage = canonicalizeLanguageName(language);
      if (normalizedLanguage) {
        languageSet.add(normalizedLanguage);
      }
    });
    if (translationTrackerData?.language) {
      languageSet.add(canonicalizeLanguageName(translationTrackerData.language));
    }
    if (bookLanguage) {
      languageSet.delete(canonicalizeLanguageName(bookLanguage));
    }
    return Array.from(languageSet);
  }, [
    availableLanguages,
    translationLanguages,
    normalizedTranslationProgress,
    translationTrackerData?.language,
    bookLanguage,
  ]);

  const glossaryScanLanguagesKey = useMemo(() => {
    return glossaryScanLanguages
      .map(language => normalizeLangKey(language))
      .sort()
      .join('|');
  }, [glossaryScanLanguages]);

  const sourceLangByTarget = useMemo(() => {
    const map = new Map<string, string>();
    (translationPairs || []).forEach(pair => {
      const { sourceLang: source, targetLang: target } = splitLanguagePair(pair);
      if (!source || !target) {
        return;
      }
      map.set(normalizeLangKey(target), source);
    });
    return map;
  }, [translationPairs]);

  useEffect(() => {
    let isActive = true;
    const resolvedUserId = (userId || '').trim();
    if (!bookId || !resolvedUserId || glossaryScanLanguages.length === 0) {
      if (Object.keys(latestTermScanByLang).length) {
        setLatestTermScanByLang({});
      }
      return () => {
        isActive = false;
      };
    }
    const fetchId = ++termScanFetchIdRef.current;

    const loadLatestRuns = async () => {
      try {
        const targetsBySource = new Map<string, string[]>();
        glossaryScanLanguages.forEach((language) => {
          const sourceLang =
            sourceLangByTarget.get(normalizeLangKey(language)) ||
            (bookLanguage || '').trim();
          if (!sourceLang) {
            return;
          }
          const existing = targetsBySource.get(sourceLang) || [];
          existing.push(language);
          targetsBySource.set(sourceLang, existing);
        });
        const matrixResponses = await Promise.all(
          Array.from(targetsBySource.entries()).map(async ([sourceLang, targetLangs]) => {
            if (!sourceLang || !targetLangs.length) {
              return null;
            }
            const res = await axios.post(`${API_BASE}/books/${bookId}/term-scan/latest-matrix`, {
              userId: resolvedUserId,
              sourceLang,
              targetLangs,
            });
            return res?.data?.matrix || null;
          }),
        );
        if (!isActive || fetchId !== termScanFetchIdRef.current) {
          return;
        }
        const next: Record<string, any> = {};
        matrixResponses.forEach((matrixPayload) => {
          if (!matrixPayload || typeof matrixPayload !== 'object') {
            return;
          }
          Object.entries(matrixPayload).forEach(([language, payload]) => {
            const run = (payload as any)?.run || null;
            if (!run) {
              return;
            }
            const key = normalizeLangKey(language);
            if (!key) {
              return;
            }
            next[key] = run;
          });
        });
        setLatestTermScanByLang(next);
      } catch (error) {
        if (!isActive || fetchId !== termScanFetchIdRef.current) {
          return;
        }
        console.error('Failed to load latest glossary scan runs', error);
      }
    };

    void loadLatestRuns();
    return () => {
      isActive = false;
    };
  }, [
    bookId,
    bookLanguage,
    glossaryScanLanguagesKey,
    sourceLangByTarget,
    userId,
  ]);

  const isOriginalDashboard = !selectedLanguage || selectedLanguage === 'original';
  const analysisStatus = (book?.characterRelationshipsAnalysisStatus || '')
    .toString()
    .trim()
    .toLowerCase();
  const analysisRunning = analysisStatus === 'queued' || analysisStatus === 'running';
  const analysisFailed = analysisStatus === 'failed';
  const analysisUiEnabled = RELATIONSHIP_UI_ENABLED && Boolean(onOpenRelationshipAnalysis);
  const hasGlossaryScanNeedsReview = (language: string) => {
    const key = normalizeLangKey(language);
    const run = latestTermScanByLang[key];
    if (!run) return false;
    const status = (run.status || '').toString().trim().toLowerCase();
    const reviewed = Boolean(run.reviewedAt || run.reviewed_at || run.reviewedBy);
    const pendingReviewCount = Number(run.pendingReviewCount ?? run.pending_review_count ?? 0);
    return TERM_SCAN_SUCCESS_STATES.has(status) && pendingReviewCount > 0 && !reviewed;
  };
  const hasGlossaryScanInFlight = (language: string) => {
    const key = normalizeLangKey(language);
    const run = latestTermScanByLang[key];
    if (!run) return false;
    const status = (run.status || '').toString().trim().toLowerCase();
    return TERM_SCAN_IN_FLIGHT_STATES.has(status);
  };
  const getLanguagePairForTarget = (language: string): string => {
    const targetKey = normalizeLangKey(language);
    const sourceKey = normalizeLangKey(bookLanguage);
    const matchingPair = (translationPairs || []).find(pair => {
      const { sourceLang, targetLang } = splitLanguagePair(pair);
      if (targetKey && normalizeLangKey(targetLang) !== targetKey) {
        return false;
      }
      if (sourceKey && normalizeLangKey(sourceLang) !== sourceKey) {
        return false;
      }
      return Boolean(sourceLang && targetLang);
    });
    if (matchingPair) {
      return matchingPair;
    }
    return bookLanguage ? `${bookLanguage}-${language}` : '';
  };
  const getGlossaryRuleCountForLanguage = (
    language: string,
    fallbackCount?: number,
  ): number => {
    const pair = getLanguagePairForTarget(language);
    const { targetLang } = splitLanguagePair(pair);
    const resolvedTargetLang = targetLang || language;
    const count = [
      resolveBookTerms(neverTranslateWords, pair),
      resolveBookTerms(alwaysTranslateWords, pair),
      resolveSeriesTerms(seriesNeverTranslateWords, resolvedTargetLang, pair),
      resolveSeriesTerms(seriesAlwaysTranslateWords, resolvedTargetLang, pair),
      resolveAccountTerms(accountNeverTranslateWords, resolvedTargetLang),
      resolveAccountTerms(accountAlwaysTranslateWords, resolvedTargetLang),
    ].reduce((total, terms) => total + terms.length, 0);
    if (count > 0) {
      return count;
    }
    return Math.max(0, fallbackCount || 0);
  };

  const analysisStepDescription = analysisRunning
    ? 'Building your Character List...'
    : analysisFailed
      ? 'Last run failed. Try again.'
    : 'Build a Character List and relationships.';
  const getFrenchDialogueDisabledReason = (): string | null => {
    if (!selectedIsFrenchPair) {
      return null;
    }
    if (!selectedFrenchEligibilityKnown) {
      return 'Checking French dialogue eligibility...';
    }
    const discountedRetranslationEligible = Boolean(
      selectedFrenchEligibility?.discountedRetranslation?.eligible,
    );
    if (selectedFrenchEligibility?.eligible || discountedRetranslationEligible) {
      return null;
    }
    const reasonCode = String(selectedFrenchEligibility?.reasonCode || '').trim().toLowerCase();
    const discountedReasonCode = String(
      selectedFrenchEligibility?.discountedRetranslation?.reasonCode || '',
    )
      .trim()
      .toLowerCase();
    if (discountedReasonCode === 'discount_offer_consumed') {
      return 'The free cleanup pass and discounted retranslation have already been used for this book.';
    }
    if (discountedReasonCode === 'run_in_progress') {
      return 'A discounted retranslation is already in progress for this book.';
    }
    if (selectedFrenchEligibility?.alreadyRan) {
      return 'The free cleanup pass has already been used.';
    }
    if (reasonCode === 'already_ran') {
      return 'The free cleanup pass has already been used.';
    }
    if (discountedRetranslationEligible && (reasonCode === 'guillemet_style' || reasonCode === 'chapter_already_completed')) {
      return 'The free cleanup pass has already been used.';
    }
    if (reasonCode === 'run_in_progress') {
      return 'A French dialogue rules run is already in progress.';
    }
    if (reasonCode === 'no_translated_chapters') {
      return 'No translated French chapters were found yet.';
    }
    if (reasonCode === 'after_cutoff') {
      return 'This translation does not require this legacy rules pass.';
    }
    if (reasonCode === 'guillemet_style') {
      return 'This translation already appears to use French guillemet dialogue style.';
    }
    if (reasonCode === 'chapter_already_completed') {
      return 'French dialogue rules were already applied for this chapter.';
    }
    if (reasonCode === 'chapter_not_translated') {
      return 'This chapter is not translated yet.';
    }
    if (reasonCode === 'all_rulesets_ineligible') {
      const detail = String(selectedFrenchEligibility?.reasonDetail || '').trim();
      if (detail) {
        return `No available ruleset can run: ${detail}.`;
      }
      return 'No available French dialogue ruleset can run for this translation.';
    }
    if (reasonCode) {
      return `This translation is not eligible (${reasonCode.replace(/_/g, ' ')}).`;
    }
    return 'This translation is not eligible for this French dialog rules pass.';
  };
  const frenchDialogueDisabledReason = getFrenchDialogueDisabledReason();
  const isFrenchDialogueQuickActionVisible = selectedIsFrenchPair;
  const isFrenchDialogueQuickActionDisabled = Boolean(frenchDialogueDisabledReason);

  const formatExportLabel = (entry: RecentExportEntry): string => {
    const versionLabel = (entry.version || 'Export').trim();
    const formatLabel = (entry.format || '').trim();
    let displayVersion = versionLabel;
    const { targetLang } = splitLanguagePair(versionLabel);
    if (targetLang) {
      displayVersion = targetLang || versionLabel;
    }
    if (!formatLabel) {
      return displayVersion || 'Export';
    }
    return `${displayVersion} (${formatLabel})`;
  };

  const formatExportDate = (entry: RecentExportEntry): string => {
    if (!entry.completedAt) {
      return 'Unknown date';
    }
    const parsed = new Date(entry.completedAt);
    if (Number.isNaN(parsed.getTime())) {
      return 'Unknown date';
    }
    return parsed.toLocaleString();
  };

  const handleDownloadExport = async (entry: RecentExportEntry) => {
    if (!entry.exportId) {
      return;
    }
    setDownloadingExportId(entry.exportId);
    try {
      await onDownloadRecentExport(entry);
    } finally {
      setDownloadingExportId(null);
    }
  };

  useEffect(() => {
    let isActive = true;
    if (!userId || !bookId) return undefined;
    const userRef = doc(db, 'users', userId);
    getDoc(userRef)
      .then(snapshot => {
        if (!isActive) return;
        const data = snapshot.data();
        const saved = data?.dashboardDismissedSteps?.[bookId];
        if (Array.isArray(saved)) {
          setDismissedSteps(new Set(saved));
        }
      })
      .catch(() => {});
    return () => {
      isActive = false;
    };
  }, [userId, bookId]);

  const persistDismissedSteps = async (next: Set<string>) => {
    if (!userId || !bookId) return;
    await axios.post(`${API_BASE}/account/preferences`, {
      dashboardDismissedSteps: { bookId, steps: Array.from(next) },
    });
  };

  const handleDismissStep = (key: string) => {
    setDismissedSteps(prev => {
      const next = new Set(prev);
      next.add(key);
      void persistDismissedSteps(next);
      return next;
    });
  };
  const handleUndoDismissStep = (key: string) => {
    setDismissedSteps(prev => {
      if (!prev.has(key)) {
        return prev;
      }
      const next = new Set(prev);
      next.delete(key);
      void persistDismissedSteps(next);
      return next;
    });
  };
  const beginDismissStep = (key: string) => {
    handleDismissStep(key);
  };

  const renderDismissControl = (stepKey: string) => (
    <Popconfirm
      title="Mark this step as done?"
      okText="Mark done"
      cancelText="Cancel"
      open={confirmStepKey === stepKey}
      onConfirm={(event) => {
        event?.stopPropagation();
        beginDismissStep(stepKey);
        setConfirmStepKey(null);
      }}
      onCancel={(event) => {
        event?.stopPropagation();
        setConfirmStepKey(null);
      }}
    >
      <Tooltip title="Mark as done">
        <span
          role="button"
          tabIndex={0}
          onClick={(event) => {
            event.stopPropagation();
            setConfirmStepKey(stepKey);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.stopPropagation();
              setConfirmStepKey(stepKey);
            }
          }}
          aria-label="Mark as done"
          className="dashboard-done-toggle"
        >
          <CheckOutlined style={{ fontSize: 12 }} />
        </span>
      </Tooltip>
    </Popconfirm>
  );
  const sourceReadinessItems = useMemo(
    () => buildSourceReadinessItems({
      hasTitle: Boolean(hasTitle),
      hasAuthor: Boolean(hasAuthor),
      hasBlurb: Boolean(hasBlurb),
      hasGenres: Boolean(hasGenres),
      hasKeywords: Boolean(hasKeywords),
      hasCover: Boolean(hasCover),
    }),
    [hasAuthor, hasBlurb, hasCover, hasGenres, hasKeywords, hasTitle],
  );
  const nextSteps = useMemo(() => {
    const steps: Array<{
      key: string;
      label: string;
      onClick: () => void;
      icon: React.ReactNode;
      cta: string;
      ctaOnClick?: () => void;
      disabled?: boolean;
      progressPercent?: number;
      detail?: string;
      isBusy?: boolean;
      busyText?: string;
    }> = [];
    const showAnalysisStep = analysisUiEnabled && isOriginalDashboard && analysisStatus !== 'completed';
    const metadataComplete = sourceReadinessItems.every(item => item.done);
    const metadataPercent = Math.round(
      (sourceReadinessItems.filter(item => item.done).length / sourceReadinessItems.length) * 100,
    );
    if (showAnalysisStep) {
      steps.push({
        key: 'character-analysis',
        label: analysisRunning
          ? 'Character analysis is running'
          : analysisFailed
            ? 'Retry character analysis'
            : 'Run character analysis',
        onClick: () => {
          if (onOpenCharactersTab) {
            onOpenCharactersTab();
          }
          if (onOpenRelationshipAnalysis) {
            onOpenRelationshipAnalysis();
            return;
          }
          onEditMetadata();
        },
        icon: <BranchesOutlined />,
        cta: 'Run analysis',
        detail: analysisStepDescription,
      });
    }
    if (!metadataComplete) {
      steps.push({
        key: 'metadata',
        label: 'Complete metadata',
        onClick: onEditMetadata,
        icon: <FormOutlined />,
        cta: 'Go now',
        progressPercent: metadataPercent,
      });
    }
    if (
      isOriginalDashboard &&
      originalPendingProofreadReviewCount > 0 &&
      onApplyPendingProofreadReviewsForLanguage
    ) {
      steps.push({
        key: 'original-proofread-pending',
        label: 'Apply pending original proofread changes',
        onClick: () => {
          if (originalPendingProofreadApplyInFlight) {
            return;
          }
          onApplyPendingProofreadReviewsForLanguage('Original');
        },
        icon: <CheckOutlined />,
        cta: 'Apply Pending',
        ctaOnClick: () => {
          if (originalPendingProofreadApplyInFlight) {
            return;
          }
          onApplyPendingProofreadReviewsForLanguage('Original');
        },
        disabled: originalPendingProofreadApplyInFlight,
        detail: `${originalPendingProofreadReviewCount} pending proofread review${originalPendingProofreadReviewCount === 1 ? '' : 's'} ready to apply.`,
      });
    }

    const inProgressCount = Object.values(normalizedTranslationProgress || {}).filter(
      entry => entry.status === 'in_progress' && entry.percentage < 100,
    ).length;

    const prioritizedMarkets = [
      'German',
      'French',
      'Italian',
      'Spanish',
      'Portuguese',
      'European Spanish',
      'Latin American Spanish',
      'Brazilian Portuguese',
      'European Portuguese',
      'Japanese',
      'Chinese',
      'Simplified Chinese',
      'Traditional Chinese',
      'Korean',
      'Russian',
      'Dutch',
      'Swedish',
      'Norwegian',
      'Polish',
      'Turkish',
      'Hindi',
      'Thai',
    ];
    const availableSet = new Set((availableLanguages || []).filter(Boolean));
    const startedTargets = new Set<string>();
    Object.entries(normalizedTranslationProgress || {}).forEach(([language, entry]) => {
      if (entry.status !== 'not_started' || entry.percentage > 0) {
        startedTargets.add(canonicalizeLanguageName(language));
      }
    });
    (translationPairs || []).forEach(pair => {
      const { targetLang } = splitLanguagePair(pair);
      if (targetLang) {
        startedTargets.add(canonicalizeLanguageName(targetLang));
      }
    });

    const buildLangKey = (value: string) => value.replace(/ /g, '0');
    const sourceKey = bookLanguage ? buildLangKey(bookLanguage) : '';

    const getMetadataStatus = (language: string) => {
      if (!book || !sourceKey) return false;
      const targetKey = buildLangKey(language);
      const hasTitle = hasDashboardMetadataValue((book as any)[`title_${sourceKey}_${targetKey}`]);
      const hasBlurb = hasDashboardMetadataValue((book as any)[`blurb_${sourceKey}_${targetKey}`]);
      const hasSeries = book.series ? hasDashboardMetadataValue((book as any)[`series_${sourceKey}_${targetKey}`]) : true;
      return hasTitle && hasBlurb && hasSeries;
    };

    const getKeywordStatus = (language: string) => {
      const pairKey = bookLanguage ? `${bookLanguage}-${language}` : '';
      return Boolean(pairKey && keywordTranslations?.[pairKey]?.length);
    };

    const getCoverStatus = (language: string) =>
      Boolean(languageCovers?.[language] || languageCovers?.[language.toLowerCase()]);

    const getTranslationStatus = (language: string) => {
      const entry = normalizedTranslationProgress?.[canonicalizeLanguageName(language)];
      if (!entry) return false;
      return entry.status === 'complete' || entry.percentage >= 100;
    };

    const shouldSkipGlossaryScan = (language: string) => {
      return getGlossaryRuleCountForLanguage(language) > 0;
    };

    const getProofreadProgress = (language: string) => {
      const total = Array.isArray(book?.chapters) ? (book?.chapters?.length || 0) : 0;
      if (!total) {
        return { count: 0, total: 0 };
      }
      let sourceLang = bookLanguage || '';
      const pair = (translationPairs || []).find(item => languagesMatch(splitLanguagePair(item || '').targetLang, language));
      if (pair) {
        sourceLang = splitLanguagePair(pair).sourceLang;
      }
      if (!sourceLang) {
        return { count: 0, total };
      }
      const count = countProofreadChaptersForLanguage({
        book,
        language,
        bookLanguage,
        translationPairs,
      });
      return { count, total };
    };

    const getPendingProofreadReviewCount = (language: string) => countPendingProofreadReviewsForLanguage({
      book,
      language,
      bookLanguage,
      translationPairs,
    });

    const getNextLanguageStep = (language: string) => {
      const pendingProofreadReviewCount = getPendingProofreadReviewCount(language);
      const glossaryScanNeedsReview = hasGlossaryScanNeedsReview(language);
      const stepsByPriority = [
        {
          key: `glossary-${language}`,
          isComplete: shouldSkipGlossaryScan(language),
          label: glossaryScanNeedsReview
            ? `Review AI glossary scan for ${language}`
            : `Run AI glossary scan for ${language}`,
          onClick: () => {
            if (hasGlossaryScanInFlight(language)) {
              message.info('AI Glossary Scan is already running for this language.');
              return;
            }
            if (glossaryScanNeedsReview && onOpenMetadataForLanguage) {
              onOpenMetadataForLanguage(language);
              return;
            }
            if (onOpenGlossaryScanForLanguage) {
              onOpenGlossaryScanForLanguage(language);
              return;
            }
            if (onOpenMetadataForLanguage) {
              onOpenMetadataForLanguage(language);
              return;
            }
            onEditMetadata();
          },
          icon: <SearchOutlined />,
          cta: glossaryScanNeedsReview ? 'Review' : 'Run',
          detail: glossaryScanNeedsReview
            ? 'Review and apply glossary suggestions before translating.'
            : 'Scan chapters for glossary terms before translating.',
          disabled: hasGlossaryScanInFlight(language),
        },
        {
          key: `book-${language}`,
          isComplete: getTranslationStatus(language),
          label: `Translate book into ${language}`,
          onClick: () => {
            if (onSelectTranslationLanguage) {
              onSelectTranslationLanguage(language);
              return;
            }
            onTranslateBook();
          },
          icon: <TranslationOutlined />,
          cta: 'Start',
          detail: `Begin translating chapters into ${language}.`,
          progressPercent: (() => {
            const progressPercent = normalizedTranslationProgress?.[canonicalizeLanguageName(language)]?.percentage;
            return typeof progressPercent === 'number' ? Math.round(progressPercent) : undefined;
          })(),
        },
        {
          key: `proof-${language}`,
          isComplete: (() => {
            const { count, total } = getProofreadProgress(language);
            if (pendingProofreadReviewCount > 0) return false;
            if (total <= 0) return true;
            return count >= total;
          })(),
          label: `Proofread the ${language} translation`,
          onClick: () => {
            if (onOpenProofreadForLanguage) {
              onOpenProofreadForLanguage(language);
              return;
            }
            onProofreadBook();
          },
          icon: <CheckOutlined />,
          cta: (() => {
            if (pendingProofreadReviewCount > 0 && onApplyPendingProofreadReviewsForLanguage) {
              return 'Apply Pending';
            }
            const { count } = getProofreadProgress(language);
            return count > 0 ? 'Continue' : 'Start';
          })(),
          ctaOnClick:
            pendingProofreadReviewCount > 0 && onApplyPendingProofreadReviewsForLanguage
              ? () => onApplyPendingProofreadReviewsForLanguage(language)
              : undefined,
          detail:
            pendingProofreadReviewCount > 0
              ? `${pendingProofreadReviewCount} pending proofread review${pendingProofreadReviewCount === 1 ? '' : 's'} ready to apply.`
              : `Review the ${language} translation for accuracy and style.`,
          progressPercent: (() => {
            const { count, total } = getProofreadProgress(language);
            if (!total) return undefined;
            return Math.round((count / total) * 100);
          })(),
        },
        {
          key: `meta-${language}`,
          isComplete: getMetadataStatus(language),
          label: `Translate metadata into ${language}`,
          onClick: () => {
            if (onOpenMetadataTranslateForLanguage) {
              onOpenMetadataTranslateForLanguage(language);
              return;
            }
            if (onOpenMetadataForLanguage) {
              onOpenMetadataForLanguage(language);
              return;
            }
            onEditMetadata();
          },
          icon: <FormOutlined />,
          cta: 'Go now',
          detail: 'Localize the title, series, and blurb.',
        },
        {
          key: `keywords-${language}`,
          isComplete: getKeywordStatus(language),
          label: `Suggest keywords for ${language}`,
          onClick: () => {
            if (onOpenKeywordSuggestionsForLanguage) {
              onOpenKeywordSuggestionsForLanguage(language);
              return;
            }
            onEditMetadata();
          },
          icon: <BulbOutlined />,
          cta: 'Suggest',
          detail: 'Create localized keywords for discoverability.',
        },
        {
          key: `cover-${language}`,
          isComplete: getCoverStatus(language),
          label: `Translate cover into ${language}`,
          onClick: () => {
            if (onOpenCoverTranslationForLanguage) {
              onOpenCoverTranslationForLanguage(language);
              return;
            }
            onEditMetadata();
          },
          icon: <EditOutlined />,
          cta: 'Start',
          detail: 'Create a localized cover for this language.',
        },
        {
          key: `audio-${language}`,
          isComplete: false,
          label: `Create audiobook for ${language}`,
          onClick: onCreateAudiobook,
          icon: <AudioOutlined />,
          cta: 'Create',
          detail: `Generate the ${language} audiobook.`,
        },
      ];

      for (const step of stepsByPriority) {
        if (step.isComplete) {
          continue;
        }
        if (dismissedSteps.has(step.key)) {
          continue;
        }
        return step;
      }
      return null;
    };

    const topMarketSteps: Array<{
      key: string;
      label: string;
      onClick: () => void;
      icon: React.ReactNode;
      cta: string;
      ctaOnClick?: () => void;
      progressPercent?: number;
      detail?: string;
    }> = [];
    prioritizedMarkets
      .filter(language => availableSet.has(language))
      .filter(language => startedTargets.has(language) || inProgressCount <= 2)
      .some(language => {
        const step = getNextLanguageStep(language);
        if (step && !dismissedSteps.has(step.key)) {
          topMarketSteps.push(step);
        }
        return topMarketSteps.length >= 5;
      });

    topMarketSteps.forEach(step => {
      steps.push(step);
    });

    return steps
      .filter(step => !dismissedSteps.has(step.key))
      .slice(0, 6);
  }, [
    availableLanguages,
    book,
    bookLanguage,
    dismissedSteps,
    keywordTranslations,
    languageCovers,
    latestTermScanByLang,
    onTranslateBook,
    onCreateAudiobook,
    onEditMetadata,
    onExportBook,
    onOpenCoverTranslationForLanguage,
    onOpenKeywordSuggestionsForLanguage,
    onOpenMetadataForLanguage,
    onOpenMetadataTranslateForLanguage,
    onOpenGlossaryScanForLanguage,
    onOpenRelationshipAnalysis,
    onOpenCharactersTab,
    onApplyPendingProofreadReviewsForLanguage,
    onSelectTranslationLanguage,
    selectedLanguage,
    originalPendingProofreadReviewCount,
    originalPendingProofreadApplyInFlight,
    analysisFailed,
    analysisRunning,
    analysisStatus,
    analysisStepDescription,
    analysisUiEnabled,
    isOriginalDashboard,
    sourceReadinessItems,
    translationPairs,
    normalizedTranslationProgress,
  ]);

  const handleOpenCharactersTab = () => {
    if (onOpenCharactersTab) {
      onOpenCharactersTab();
    }
  };

  const handleRunGlossaryScanForSelectedLanguage = () => {
    if (!selectedTargetLanguage) {
      return;
    }
    if (hasGlossaryScanInFlight(selectedTargetLanguage)) {
      message.info('AI Glossary Scan is already running for this language.');
      return;
    }
    if (hasGlossaryScanNeedsReview(selectedTargetLanguage) && onOpenMetadataForLanguage) {
      onOpenMetadataForLanguage(selectedTargetLanguage);
      return;
    }
    if (onOpenGlossaryScanForLanguage) {
      onOpenGlossaryScanForLanguage(selectedTargetLanguage);
      return;
    }
    if (onOpenMetadataForLanguage) {
      onOpenMetadataForLanguage(selectedTargetLanguage);
      return;
    }
    onEditMetadata();
  };

  const languageStepCards = useMemo((): Array<{
    key: string;
    title: string;
    label: string;
    onClick: () => void;
    cta: string;
    ctaOnClick?: () => void;
    icon: React.ReactNode;
    progressPercent?: number;
    isComplete: boolean;
    detail?: string;
    disabled?: boolean;
    isBusy?: boolean;
    busyText?: string;
    highlightAction?: boolean;
    pendingReviewCount?: number;
  }> => {
    if (!translationTrackerData?.language) {
      return [];
    }
    const {
      language,
      totalChapters,
      translatedCount,
      proofreadCount,
      pendingProofreadReviewCount: pendingProofreadReviewCountFromTracker,
      metadataStatus,
      keywordCount,
      coverStatus,
      audiobookStatus,
    } = translationTrackerData;
    const safeTotal = Math.max(totalChapters, 0);
    const pendingProofreadReviewCount = pendingProofreadReviewCountFromTracker ?? countPendingProofreadReviewsForLanguage({
      book,
      language,
      bookLanguage,
      translationPairs,
    });
    const isApplyingPendingForStep = isApplyingPendingProofreadForLanguage(language);
    const translatedDone = safeTotal === 0 ? true : translatedCount >= safeTotal;
    const glossaryRuleCount = getGlossaryRuleCountForLanguage(language, translationTrackerData.glossaryTermCount);
    const glossaryReady = glossaryRuleCount > 0;
    const glossaryScanInFlight = hasGlossaryScanInFlight(language);
    const glossaryScanNeedsReview = hasGlossaryScanNeedsReview(language);
    const proofreadDone =
      pendingProofreadReviewCount > 0
        ? false
        : safeTotal === 0
          ? true
          : proofreadCount >= safeTotal;
    const metadataDone = Boolean(metadataStatus.title && metadataStatus.series && metadataStatus.blurb);
    const keywordsDone = keywordCount > 0;
    const coverDone = Boolean(coverStatus);
    const audiobookDone = Boolean(audiobookStatus);
    const metadataPercent = Math.round(
      ((Number(metadataStatus.title) + Number(metadataStatus.series) + Number(metadataStatus.blurb)) / 3) * 100,
    );

    const steps = [
      {
        key: `lang-${language}-glossary`,
        title: 'AI glossary scan',
        label: glossaryReady
          ? `${glossaryRuleCount} glossary rule${glossaryRuleCount === 1 ? '' : 's'} ready for ${language}.`
          : glossaryScanNeedsReview
            ? `Review and apply ${language} glossary suggestions before translating.`
            : `Scan chapters for ${language} glossary terms before translating.`,
        onClick: () => {
          if (glossaryScanInFlight) {
            message.info('AI Glossary Scan is already running for this language.');
            return;
          }
          if (glossaryScanNeedsReview && onOpenMetadataForLanguage) {
            onOpenMetadataForLanguage(language);
            return;
          }
          if (onOpenGlossaryScanForLanguage) {
            onOpenGlossaryScanForLanguage(language);
            return;
          }
          if (onOpenMetadataForLanguage) {
            onOpenMetadataForLanguage(language);
            return;
          }
          onEditMetadata();
        },
        cta: glossaryScanNeedsReview ? 'Review scan' : 'Run scan',
        icon: <SearchOutlined />,
        isComplete: glossaryReady,
        disabled: glossaryScanInFlight,
      },
      {
        key: `lang-${language}-translate`,
        title: 'Translate book',
        label: `Translate the book into ${language}.`,
        onClick: () => {
          if (onSelectTranslationLanguage) {
            onSelectTranslationLanguage(language);
            return;
          }
          onTranslateBook();
        },
        cta: translatedCount > 0 ? 'Continue translation' : 'Start translation',
        icon: <TranslationOutlined />,
        progressPercent: safeTotal > 0 ? Math.round((translatedCount / safeTotal) * 100) : undefined,
        isComplete: translatedDone,
      },
      {
        key: `lang-${language}-proofread`,
        title: 'Proofread',
        label: `Proofread the ${language} translation.`,
        onClick: () => {
          if (isApplyingPendingForStep) {
            return;
          }
          if (onOpenProofreadForLanguage) {
            onOpenProofreadForLanguage(language);
            return;
          }
          onProofreadBook();
        },
        cta:
          pendingProofreadReviewCount > 0 && onApplyPendingProofreadReviewsForLanguage
            ? 'Apply Pending'
            : proofreadCount > 0
              ? 'Continue proofread'
              : 'Start proofread',
        ctaOnClick:
          pendingProofreadReviewCount > 0 && onApplyPendingProofreadReviewsForLanguage
            ? () => {
                if (isApplyingPendingForStep) {
                  return;
                }
                onApplyPendingProofreadReviewsForLanguage(language);
              }
            : undefined,
        icon: <CheckOutlined />,
        progressPercent: safeTotal > 0 ? Math.round((proofreadCount / safeTotal) * 100) : undefined,
        isComplete: proofreadDone,
        detail:
          pendingProofreadReviewCount > 0
            ? isApplyingPendingForStep
              ? undefined
              : `${pendingProofreadReviewCount} pending proofread review${pendingProofreadReviewCount === 1 ? '' : 's'} ready to apply.`
            : undefined,
        isBusy: isApplyingPendingForStep,
        busyText: `Applying pending proofreading changes for ${language}.`,
        disabled: isApplyingPendingForStep,
        highlightAction: pendingProofreadReviewCount > 0,
        pendingReviewCount: pendingProofreadReviewCount,
      },
      {
        key: `lang-${language}-metadata`,
        title: 'Translate metadata',
        label: `Localize title, blurb, and series for ${language}.`,
        onClick: () => {
          if (onOpenMetadataTranslateForLanguage) {
            onOpenMetadataTranslateForLanguage(language);
            return;
          }
          if (onOpenMetadataForLanguage) {
            onOpenMetadataForLanguage(language);
            return;
          }
          onEditMetadata();
        },
        cta: metadataPercent > 0 ? 'Continue metadata' : 'Translate metadata',
        icon: <FormOutlined />,
        progressPercent: metadataDone ? 100 : metadataPercent,
        isComplete: metadataDone,
      },
      {
        key: `lang-${language}-keywords`,
        title: 'Suggest Keywords',
        label: `Create localized keywords for ${language}.`,
        onClick: () => {
          if (onOpenKeywordSuggestionsForLanguage) {
            onOpenKeywordSuggestionsForLanguage(language);
            return;
          }
          onEditMetadata();
        },
        cta: 'Suggest keywords',
        icon: <BulbOutlined />,
        isComplete: keywordsDone,
      },
      {
        key: `lang-${language}-cover`,
        title: 'Translate cover',
        label: `Create a localized cover for ${language}.`,
        onClick: () => {
          if (onOpenCoverTranslationForLanguage) {
            onOpenCoverTranslationForLanguage(language);
            return;
          }
          onEditMetadata();
        },
        cta: 'Translate cover',
        icon: <EditOutlined />,
        isComplete: coverDone,
      },
      {
        key: `lang-${language}-audiobook`,
        title: 'Create audiobook',
        label: `Generate the ${language} audiobook.`,
        onClick: onCreateAudiobook,
        cta: 'Create audiobook',
        icon: <AudioOutlined />,
        isComplete: audiobookDone,
      },
    ];

    return steps;
  }, [
    book,
    bookLanguage,
    latestTermScanByLang,
    normalizedApplyingPendingProofreadLanguage,
    onCreateAudiobook,
    onEditMetadata,
    onOpenCoverTranslationForLanguage,
    onOpenGlossaryScanForLanguage,
    onOpenKeywordSuggestionsForLanguage,
    onOpenMetadataForLanguage,
    onOpenMetadataTranslateForLanguage,
    onOpenProofreadForLanguage,
    onApplyPendingProofreadReviewsForLanguage,
    onProofreadBook,
    onSelectTranslationLanguage,
    onTranslateBook,
    translationTrackerData,
    translationPairs,
  ]);

  const renderPendingProofreadNotice = (
    language: string,
    pendingCount: number,
    onApply: () => void,
    options?: { compact?: boolean; disabled?: boolean },
  ) => (
    <div
      style={{
        display: 'flex',
        alignItems: options?.compact ? 'flex-start' : 'center',
        justifyContent: 'space-between',
        gap: 12,
        width: '100%',
        padding: options?.compact ? '10px 12px' : '12px 14px',
        borderRadius: 12,
        border: '1px solid var(--status-warning-border)',
        background: 'var(--status-warning-bg)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, minWidth: 0, flex: 1 }}>
        <ExclamationCircleOutlined style={{ color: 'var(--status-warning-text)', fontSize: 16, marginTop: 2 }} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
          <span style={{ color: 'var(--text-primary)', fontSize: 13, fontWeight: 600 }}>
            Pending proofreading changes are ready
          </span>
          <span style={{ color: 'var(--text-secondary)', fontSize: 12, lineHeight: 1.45 }}>
            {`${pendingCount} chapter${pendingCount === 1 ? '' : 's'} in ${language} still need their proofreading changes applied.`}
          </span>
        </div>
      </div>
      <Button
        type="primary"
        className="app-primary-cta"
        size={options?.compact ? 'small' : 'middle'}
        onClick={onApply}
        disabled={options?.disabled}
        style={{
          flexShrink: 0,
          minWidth: options?.compact ? 124 : 168,
        }}
      >
        {`Apply Pending (${pendingCount})`}
      </Button>
    </div>
  );
  const manualLanguageSteps = useMemo(() => {
    if (!translationTrackerData?.language) {
      return [];
    }
    const language = translationTrackerData.language;
    const steps = [
      { key: `lang-${language}-glossary`, label: 'AI glossary scan' },
      { key: `lang-${language}-translate`, label: 'Translate book' },
      { key: `lang-${language}-proofread`, label: 'Proofread translation' },
      { key: `lang-${language}-metadata`, label: 'Translate metadata' },
      { key: `lang-${language}-keywords`, label: 'Suggest Keywords' },
      { key: `lang-${language}-cover`, label: 'Translate cover' },
      { key: `lang-${language}-audiobook`, label: 'Create audiobook' },
    ];
    return steps.filter(step => dismissedSteps.has(step.key));
  }, [dismissedSteps, translationTrackerData?.language]);
  const handleFlagClick = (language: string) => {
    if (onSelectDashboardLanguage) {
      onSelectDashboardLanguage(language);
      return;
    }
    if (onSelectTranslationLanguage) {
      onSelectTranslationLanguage(language);
      return;
    }
  };

  const getLanguageCoverUrl = (language: string): string | undefined => {
    return languageCovers?.[language] || languageCovers?.[language.toLowerCase()] || undefined;
  };
  const getTranslatedCountForLanguage = (language: string): { translatedCount: number; totalChapters: number } => {
    const entry = normalizedTranslationProgress?.[canonicalizeLanguageName(language)];
    const totalChapters = Array.isArray(book?.chapters) ? book?.chapters?.length || 0 : entry?.total_chapters || 0;
    if (entry) {
      return {
        translatedCount: Math.min(entry.translated_chapters || 0, entry.total_chapters || totalChapters),
        totalChapters: entry.total_chapters || totalChapters,
      };
    }
    return { translatedCount: 0, totalChapters };
  };

  const getMetadataDoneForLanguage = (language: string): boolean => {
    if (!book || !bookLanguage) return false;
    const buildLangKey = (value: string) => value.replace(/ /g, '0');
    const sourceKey = buildLangKey(bookLanguage);
    const targetKey = buildLangKey(language);
    const titleDone = hasDashboardMetadataValue((book as any)[`title_${sourceKey}_${targetKey}`]);
    const blurbDone = hasDashboardMetadataValue((book as any)[`blurb_${sourceKey}_${targetKey}`]);
    const seriesDone = book.series ? hasDashboardMetadataValue((book as any)[`series_${sourceKey}_${targetKey}`]) : true;
    return titleDone && blurbDone && seriesDone;
  };

  const getKeywordsDoneForLanguage = (language: string): boolean => {
    const pairKey = bookLanguage ? `${bookLanguage}-${language}` : '';
    return Boolean(pairKey && keywordTranslations?.[pairKey]?.length);
  };

  const languageCards = useMemo<LanguageStatusCardViewModel[]>(() => {
    const languageSet = new Set<string>();
    (translationLanguages || []).forEach(language => {
      const normalized = canonicalizeLanguageName(language);
      if (normalized && normalized !== canonicalizeLanguageName(bookLanguage || '')) {
        languageSet.add(normalized);
      }
    });
    Object.keys(normalizedTranslationProgress || {}).forEach(language => {
      const normalized = canonicalizeLanguageName(language);
      if (normalized && normalized !== canonicalizeLanguageName(bookLanguage || '')) {
        languageSet.add(normalized);
      }
    });
    (translationPairs || []).forEach(pair => {
      const { targetLang } = splitLanguagePair(pair);
      const normalized = canonicalizeLanguageName(targetLang);
      if (normalized) {
        languageSet.add(normalized);
      }
    });

    return Array.from(languageSet)
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
      .map(language => {
        const languageKey = normalizeLangKey(language) || language;
        if (!languageOrderRef.current.has(languageKey)) {
          languageOrderRef.current.set(languageKey, nextLanguageOrderRef.current);
          nextLanguageOrderRef.current += 1;
        }
        const { translatedCount, totalChapters } = getTranslatedCountForLanguage(language);
        const proofreadCount = countProofreadChaptersForLanguage({
          book,
          language,
          bookLanguage,
          translationPairs,
        });
        const pendingProofreadReviewCount = countPendingProofreadReviewsForLanguage({
          book,
          language,
          bookLanguage,
          translationPairs,
        });
        return {
          language,
          sortOrder: languageOrderRef.current.get(languageKey) ?? Number.MAX_SAFE_INTEGER,
          coverUrl: getLanguageCoverUrl(language),
          translatedCount,
          totalChapters,
          proofreadCount,
          pendingProofreadReviewCount,
          metadataDone: getMetadataDoneForLanguage(language),
          keywordsDone: getKeywordsDoneForLanguage(language),
          coverDone: Boolean(getLanguageCoverUrl(language)),
          audiobookDone: Boolean((book as any)?.[`audiobook_${language}`]),
          glossaryReady: getGlossaryRuleCountForLanguage(language) > 0,
          glossaryScanInFlight: hasGlossaryScanInFlight(language),
          glossaryScanNeedsReview: hasGlossaryScanNeedsReview(language),
          onOpenWorkspace: () => {
            if (onSelectDashboardLanguage) {
              onSelectDashboardLanguage(language);
              return;
            }
            if (onSelectTranslationLanguage) {
              onSelectTranslationLanguage(language);
            }
          },
          onOpenGlossary:
            onOpenGlossaryScanForLanguage
              ? () => onOpenGlossaryScanForLanguage(language)
              : undefined,
          onOpenMetadata:
            onOpenMetadataTranslateForLanguage || onOpenMetadataForLanguage
              ? () => {
                  if (onOpenMetadataTranslateForLanguage) {
                    onOpenMetadataTranslateForLanguage(language);
                    return;
                  }
                  onOpenMetadataForLanguage?.(language);
                }
              : undefined,
          onOpenKeywords:
            onOpenKeywordSuggestionsForLanguage
              ? () => onOpenKeywordSuggestionsForLanguage(language)
              : undefined,
          onOpenCover:
            onOpenCoverTranslationForLanguage
              ? () => onOpenCoverTranslationForLanguage(language)
              : undefined,
          onApplyPending:
            pendingProofreadReviewCount > 0 && onApplyPendingProofreadReviewsForLanguage
              ? () => onApplyPendingProofreadReviewsForLanguage(language)
              : undefined,
          isApplyingPending: isApplyingPendingProofreadForLanguage(language),
        };
      });
  }, [
    book,
    bookLanguage,
    keywordTranslations,
    languageCovers,
    normalizedApplyingPendingProofreadLanguage,
    normalizedTranslationProgress,
    onApplyPendingProofreadReviewsForLanguage,
    onOpenCoverTranslationForLanguage,
    onOpenGlossaryScanForLanguage,
    onOpenKeywordSuggestionsForLanguage,
    onOpenMetadataForLanguage,
    onOpenMetadataTranslateForLanguage,
    onSelectDashboardLanguage,
    onSelectTranslationLanguage,
    translationLanguages,
    translationPairs,
  ]);

  const renderExportCard = (entry: RecentExportEntry, compact = false) => (
    <div className={compact ? 'dashboard-export-card dashboard-export-card--compact' : 'dashboard-export-card'}>
      <div>
        <strong>{formatExportLabel(entry)}</strong>
        <span>{entry.isStale ? 'Translation changed; rebuild required' : `Created ${formatExportDate(entry)}`}</span>
      </div>
      <Button
        type="default"
        size="small"
        onClick={() => {
          if (entry.isStale) {
            onExportBook();
            return;
          }
          void handleDownloadExport(entry);
        }}
        loading={downloadingExportId === entry.exportId}
      >
        {entry.isStale ? 'Re-export' : 'Download'}
      </Button>
    </div>
  );

  const baseUtilityActions: DashboardUtilityAction[] = [
    {
      key: 'edit-metadata',
      label: 'Edit metadata',
      icon: <EditOutlined />,
      onClick: onEditMetadata,
    },
    ...(isOriginalDashboard && onAddLanguage
      ? [{
          key: 'add-language',
          label: 'Add language',
          icon: <PlusOutlined />,
          onClick: onAddLanguage,
        }]
      : []),
    ...(onOpenCharactersTab
      ? [{
          key: 'characters',
          label: 'View characters',
          icon: <BranchesOutlined />,
          onClick: handleOpenCharactersTab,
        }]
      : []),
    ...(isOriginalDashboard
      ? [
          {
            key: 'translate-book',
            label: 'Translate book',
            icon: <TranslationOutlined />,
            onClick: onTranslateBook,
          },
          {
            key: 'proofread-book',
            label: 'Proofread whole book',
            icon: <CheckOutlined />,
            onClick: onProofreadBook,
          },
        ]
      : []),
    {
      key: 'upload',
      label: 'Upload chapters',
      icon: <UploadOutlined />,
      onClick: onUploadChapters,
      disabled: isUploadDisabled,
      disabledReason: uploadDisabledReason,
      tourId: 'metadata-upload-chapters',
    },
    {
      key: 'audiobook',
      label: 'Create audiobook',
      icon: <AudioOutlined />,
      onClick: onCreateAudiobook,
    },
    {
      key: 'export',
      label: 'Export book',
      icon: <ExportOutlined />,
      onClick: onExportBook,
    },
    {
      key: 'support',
      label: 'Support articles',
      icon: <QuestionCircleOutlined />,
      onClick: () => navigate('/support?tab=articles'),
    },
  ];

  const workspaceLanguage = translationTrackerData?.language || selectedTargetLanguage || 'Language';
  const activeLanguageSteps = !isOriginalDashboard
    ? languageStepCards.filter(step => !step.isComplete && !dismissedSteps.has(step.key))
    : [];
  const recommendedStep = !isOriginalDashboard
    ? (
        activeLanguageSteps.find(step => step.pendingReviewCount && step.pendingReviewCount > 0) ||
        activeLanguageSteps[0] ||
        null
      )
    : null;

  const getStepBySuffix = (suffix: string) =>
    languageStepCards.find(step => step.key.endsWith(`-${suffix}`));

  const toWorkstreamRow = (
    step: DashboardStep | undefined,
    fallback: Pick<DashboardWorkstreamRow, 'key' | 'title' | 'detail' | 'status' | 'statusTone'>,
  ): DashboardWorkstreamRow => {
    if (!step) {
      return fallback;
    }
    const dismissed = dismissedSteps.has(step.key);
    const complete = step.isComplete || dismissed;
    const isRecommended = recommendedStep?.key === step.key;
    return {
      key: step.key,
      title: step.title || fallback.title,
      status: complete ? 'Ready' : isRecommended ? 'Next' : fallback.status,
      statusTone: complete ? 'ready' : isRecommended ? 'attention' : fallback.statusTone,
      detail: step.detail || step.label || fallback.detail,
      actionLabel: complete ? undefined : step.cta,
      onAction: step.ctaOnClick || step.onClick,
      disabled: step.disabled || step.isBusy,
      progressPercent: step.progressPercent,
      dismissKey: complete ? undefined : step.key,
    };
  };

  const languageReadiness = useMemo(() => {
    if (!translationTrackerData?.language) {
      return { label: 'Needs setup', tone: 'blocked' as DashboardStatusTone };
    }
    const total = Math.max(translationTrackerData.totalChapters || 0, 0);
    const translated = Math.min(Math.max(translationTrackerData.translatedCount || 0, 0), total);
    const proofread = Math.min(Math.max(translationTrackerData.proofreadCount || 0, 0), total);
    const pending = translationTrackerData.pendingProofreadReviewCount || selectedPendingProofreadReviewCount;
    const translatedDone = total > 0 ? translated >= total : translated > 0;
    const proofreadDone = pending > 0 ? false : total > 0 ? proofread >= total : proofread > 0;
    const metadataDone = Boolean(
      translationTrackerData.metadataStatus?.title &&
      translationTrackerData.metadataStatus?.series &&
      translationTrackerData.metadataStatus?.blurb,
    );
    const keywordsDone = (translationTrackerData.keywordCount || 0) > 0;
    const coverDone = Boolean(translationTrackerData.coverStatus);
    const glossaryReady = getGlossaryRuleCountForLanguage(
      translationTrackerData.language,
      translationTrackerData.glossaryTermCount,
    ) > 0;
    if (translatedDone && proofreadDone && glossaryReady && metadataDone && keywordsDone && coverDone) {
      return { label: 'Complete', tone: 'ready' as DashboardStatusTone };
    }
    if (translatedDone && proofreadDone) {
      return { label: 'Manuscript ready', tone: 'ready' as DashboardStatusTone };
    }
    const hasProgress =
      translated > 0 ||
      proofread > 0 ||
      [
        translationTrackerData.metadataStatus?.title,
        translationTrackerData.metadataStatus?.series,
        translationTrackerData.metadataStatus?.blurb,
      ].some(Boolean) ||
      (translationTrackerData.keywordCount || 0) > 0 ||
      glossaryReady ||
      Boolean(translationTrackerData.coverStatus || translationTrackerData.audiobookStatus);
    return hasProgress
      ? { label: 'In progress', tone: 'attention' as DashboardStatusTone }
      : { label: 'Needs setup', tone: 'blocked' as DashboardStatusTone };
  }, [selectedPendingProofreadReviewCount, translationTrackerData]);

  const languageWorkstreams = useMemo<DashboardWorkstream[]>(() => {
    const translatedCount = Math.max(translationTrackerData?.translatedCount || 0, 0);
    const exportReady = translatedCount > 0;
    const exportRow: DashboardWorkstreamRow = {
      key: 'export-book',
      title: 'Export book',
      status: exportReady ? 'Ready' : 'Not available yet',
      statusTone: exportReady ? 'ready' : 'blocked',
      detail: exportReady
        ? `Export the translated ${workspaceLanguage} chapters that are ready.`
        : '',
      actionLabel: 'Export book',
      onAction: onExportBook,
      disabled: !exportReady,
    };
    const workstreams: DashboardWorkstream[] = [
      {
        key: 'manuscript',
        title: 'Manuscript',
        description: 'Prepare the translated chapters and proofread pass.',
        rows: [
          toWorkstreamRow(getStepBySuffix('glossary'), {
            key: 'glossary',
            title: 'AI glossary scan',
            status: 'Ready',
            statusTone: 'ready',
            detail: 'Glossary guidance is available or no longer suggested.',
          }),
          toWorkstreamRow(getStepBySuffix('translate'), {
            key: 'translate',
            title: 'Translate book',
            status: 'Needed',
            statusTone: 'blocked',
            detail: `Translate chapters into ${workspaceLanguage}.`,
          }),
          toWorkstreamRow(getStepBySuffix('proofread'), {
            key: 'proofread',
            title: 'Proofread',
            status: 'Recommended',
            statusTone: 'attention',
            detail: `Improve the ${workspaceLanguage} manuscript before publishing.`,
          }),
        ],
      },
      {
        key: 'storefront',
        title: 'Store listing',
        description: 'Prepare the title, blurb, keywords, and cover readers see in stores.',
        rows: [
          toWorkstreamRow(getStepBySuffix('metadata'), {
            key: 'metadata',
            title: 'Listing metadata',
            status: 'Recommended',
            statusTone: 'attention',
            detail: `Localize title, series, and blurb for ${workspaceLanguage}.`,
          }),
          toWorkstreamRow(getStepBySuffix('keywords'), {
            key: 'keywords',
            title: 'Keywords',
            status: 'Recommended',
            statusTone: 'attention',
            detail: `Create localized search keywords for ${workspaceLanguage}.`,
          }),
          toWorkstreamRow(getStepBySuffix('cover'), {
            key: 'cover',
            title: 'Cover',
            status: 'Recommended',
            statusTone: 'attention',
            detail: `Create the localized ${workspaceLanguage} cover.`,
          }),
        ],
      },
      {
        key: 'production',
        title: 'Production',
        description: 'Create optional audio and export the finished edition.',
        rows: [
          toWorkstreamRow(getStepBySuffix('audiobook'), {
            key: 'audiobook',
            title: 'Audiobook',
            status: 'Optional',
            statusTone: 'muted',
            detail: `Generate the optional ${workspaceLanguage} audiobook.`,
          }),
          exportRow,
        ],
      },
    ];

    if (!recommendedStep?.key) {
      return workstreams;
    }
    const activeIndex = workstreams.findIndex(workstream =>
      workstream.rows.some(row => row.key === recommendedStep.key),
    );
    if (activeIndex <= 0) {
      return workstreams;
    }
    const activeWorkstream = workstreams[activeIndex];
    return [
      activeWorkstream,
      ...workstreams.slice(0, activeIndex),
      ...workstreams.slice(activeIndex + 1),
    ];
  }, [
    dismissedSteps,
    languageStepCards,
    onExportBook,
    recommendedStep?.key,
    translationTrackerData?.translatedCount,
    workspaceLanguage,
  ]);

  const languageUtilityActions: DashboardUtilityAction[] = useMemo(() => {
    if (isOriginalDashboard) {
      return [];
    }
    return [
      ...baseUtilityActions.filter(action => ['edit-metadata', 'characters', 'upload', 'support'].includes(action.key)),
      ...(selectedTargetLanguage
        ? [{
            key: 'glossary-language',
            signature: selectedTargetLanguage,
            label: hasGlossaryScanInFlight(selectedTargetLanguage)
              ? 'Scan running'
              : hasGlossaryScanNeedsReview(selectedTargetLanguage)
                ? 'Review glossary'
                : getGlossaryRuleCountForLanguage(selectedTargetLanguage) > 0
                  ? 'Glossary ready'
                  : 'Glossary scan',
            icon: <SearchOutlined />,
            onClick: handleRunGlossaryScanForSelectedLanguage,
            disabled: hasGlossaryScanInFlight(selectedTargetLanguage),
            disabledReason: hasGlossaryScanInFlight(selectedTargetLanguage)
              ? 'AI Glossary Scan is already running for this language.'
              : undefined,
          }]
        : []),
      ...(selectedTargetLanguage && onOpenKeywordSuggestionsForLanguage
        ? [{
            key: 'keywords-language',
            signature: selectedTargetLanguage,
            label: 'Suggest keywords',
            icon: <BulbOutlined />,
            onClick: () => onOpenKeywordSuggestionsForLanguage(selectedTargetLanguage),
          }]
        : []),
      {
        key: 'audiobook-language',
        signature: selectedTargetLanguage || workspaceLanguage,
        label: 'Create audiobook',
        icon: <AudioOutlined />,
        onClick: onCreateAudiobook,
      },
      ...(isFrenchDialogueQuickActionVisible
        ? [{
            key: 'french-dialogue',
            signature: selectedLanguagePair || selectedTargetLanguage || '',
            label: 'Apply Updated French Dialog Rules',
            icon: <TranslationOutlined />,
            onClick: () => {
              if (!selectedLanguagePair || !onRunFrenchDialogueLegacyCorrection) return;
              onRunFrenchDialogueLegacyCorrection({
                pair: selectedLanguagePair,
                targetLanguage: selectedTargetLanguage || 'French',
              });
            },
            disabled: isFrenchDialogueQuickActionDisabled,
            disabledReason: frenchDialogueDisabledReason || undefined,
          }]
        : []),
      ...(manualLanguageSteps.length
        ? [{
            key: 'undo-manual',
            signature: manualLanguageSteps.map(step => step.key).join('|'),
            label: 'Undo manual marks',
            items: manualLanguageSteps.map(step => ({
              key: step.key,
              label: `Undo ${step.label}`,
            })),
            onItemClick: handleUndoDismissStep,
          }]
        : []),
    ];
  }, [
    baseUtilityActions,
    frenchDialogueDisabledReason,
    handleRunGlossaryScanForSelectedLanguage,
    handleUndoDismissStep,
    isFrenchDialogueQuickActionDisabled,
    isFrenchDialogueQuickActionVisible,
    isOriginalDashboard,
    manualLanguageSteps,
    onCreateAudiobook,
    onOpenKeywordSuggestionsForLanguage,
    onRunFrenchDialogueLegacyCorrection,
    selectedLanguagePair,
    selectedTargetLanguage,
  ]);

  const translatedDashboardQuickActions: DashboardUtilityAction[] = useMemo(() => {
    if (isOriginalDashboard) {
      return [];
    }
    const actions: DashboardUtilityAction[] = [
      {
        key: 'edit-metadata',
        label: 'Edit Metadata',
        icon: <EditOutlined />,
        onClick: onEditMetadata,
      },
    ];
    if (onOpenCharactersTab) {
      actions.push({
        key: 'characters',
        label: 'View Characters',
        icon: <BranchesOutlined />,
        onClick: handleOpenCharactersTab,
      });
    }
    actions.push(
      {
        key: 'translate-book',
        label: 'Translate Book',
        icon: <TranslationOutlined />,
        onClick: () => {
          if (selectedTargetLanguage && onSelectTranslationLanguage) {
            onSelectTranslationLanguage(selectedTargetLanguage);
            return;
          }
          onTranslateBook();
        },
      },
      {
        key: 'proofread-book',
        label: 'Proofread whole book',
        icon: <CheckOutlined />,
        onClick: () => {
          if (selectedTargetLanguage && onOpenProofreadForLanguage) {
            onOpenProofreadForLanguage(selectedTargetLanguage);
            return;
          }
          onProofreadBook();
        },
      },
    );
    if (selectedTargetLanguage) {
      actions.push({
        key: 'glossary-language',
        signature: selectedTargetLanguage,
        label: hasGlossaryScanInFlight(selectedTargetLanguage)
          ? 'Scan Running'
          : hasGlossaryScanNeedsReview(selectedTargetLanguage)
            ? 'Review Glossary'
            : 'Run AI Glossary Scan',
        icon: <SearchOutlined />,
        onClick: handleRunGlossaryScanForSelectedLanguage,
        disabled: hasGlossaryScanInFlight(selectedTargetLanguage),
        disabledReason: hasGlossaryScanInFlight(selectedTargetLanguage)
          ? 'AI Glossary Scan is already running for this language.'
          : undefined,
      });
    }
    if (selectedTargetLanguage && onOpenKeywordSuggestionsForLanguage) {
      actions.push({
        key: 'keywords-language',
        signature: selectedTargetLanguage,
        label: 'Suggest Keywords',
        icon: <BulbOutlined />,
        onClick: () => onOpenKeywordSuggestionsForLanguage(selectedTargetLanguage),
      });
    }
    actions.push(
      {
        key: 'audiobook-language',
        signature: selectedTargetLanguage || workspaceLanguage,
        label: 'Create Audiobook',
        icon: <AudioOutlined />,
        onClick: onCreateAudiobook,
      },
      {
        key: 'export',
        label: 'Export Book',
        icon: <ExportOutlined />,
        onClick: onExportBook,
        tourId: 'export-book',
      },
    );
    return actions;
  }, [
    handleOpenCharactersTab,
    handleRunGlossaryScanForSelectedLanguage,
    isOriginalDashboard,
    isUploadDisabled,
    navigate,
    onCreateAudiobook,
    onEditMetadata,
    onExportBook,
    onOpenCharactersTab,
    onOpenKeywordSuggestionsForLanguage,
    onOpenProofreadForLanguage,
    onProofreadBook,
    onSelectTranslationLanguage,
    onTranslateBook,
    onUploadChapters,
    selectedTargetLanguage,
    uploadDisabledReason,
    workspaceLanguage,
  ]);

  useEffect(() => {
    onDashboardOverflowActionsChange?.(languageUtilityActions);
  }, [languageUtilityActions, onDashboardOverflowActionsChange]);

  const workspaceSummaryText = (() => {
    if (!translationTrackerData) {
      return `Use this dashboard to make the ${workspaceLanguage} edition publishable.`;
    }
    const pending = translationTrackerData.pendingProofreadReviewCount || selectedPendingProofreadReviewCount;
    if (pending > 0) {
      return `${pending} pending proofread review${pending === 1 ? '' : 's'} should be applied before finalizing ${workspaceLanguage}.`;
    }
    const total = Math.max(translationTrackerData.totalChapters || 0, 0);
    const translated = Math.min(translationTrackerData.translatedCount || 0, total);
    if (translated > 0) {
      return 'Export is available; the other workstreams improve the final edition.';
    }
    return total > 0 ? '' : `Set up the ${workspaceLanguage} translation before exporting this edition.`;
  })();
  const workspaceTotalChapters = Math.max(
    translationTrackerData?.totalChapters || (Array.isArray(book?.chapters) ? book?.chapters?.length || 0 : 0),
    0,
  );
  const workspaceTranslatedCount = Math.min(
    Math.max(translationTrackerData?.translatedCount || 0, 0),
    workspaceTotalChapters,
  );
  const workspaceProofreadCount = Math.min(
    Math.max(translationTrackerData?.proofreadCount || 0, 0),
    workspaceTotalChapters,
  );
  const workspaceLanguageCoverUrl = getLanguageCoverUrl(workspaceLanguage);
  const workspaceFallbackCoverUrl = workspaceLanguageCoverUrl ? undefined : book?.coverUrl;
  const workspaceCoverInitials = workspaceLanguage.slice(0, 2).toUpperCase() || 'LA';
  const workspaceRecentExports = useMemo(
    () => recentExports.filter(entry => exportMatchesLanguage(entry, workspaceLanguage)),
    [recentExports, workspaceLanguage],
  );
  const originalPendingProofreadNotice = (
    isOriginalDashboard &&
    originalPendingProofreadReviewCount > 0 &&
    onApplyPendingProofreadReviewsForLanguage
  )
    ? renderPendingProofreadNotice(
        'Original',
        originalPendingProofreadReviewCount,
        () => onApplyPendingProofreadReviewsForLanguage('Original'),
        { disabled: originalPendingProofreadApplyInFlight },
      )
    : null;

  if (isOriginalDashboard) {
    return (
      <div className="book-dashboard-tab" data-tour-id="dashboard-container">
        <BookOverviewDashboard
          completenessItems={sourceReadinessItems}
          languageCards={languageCards}
          pendingProofreadNotice={originalPendingProofreadNotice}
          recentExports={recentExports}
          onRefreshProgress={onRefreshProgress}
          isRefreshingProgress={isRefreshingProgress}
          showFullLanguageFlags={showTranslationProgress}
          utilityActions={baseUtilityActions}
          renderExportCard={renderExportCard}
          book={book}
          bookLanguage={bookLanguage}
          onOpenOriginalCoverGeneration={
            onOpenCoverTranslationForLanguage
              ? () => onOpenCoverTranslationForLanguage('Original')
              : undefined
          }
          onOpenOriginalCoverHistory={
            onOpenCoverHistoryForLanguage
              ? () => onOpenCoverHistoryForLanguage('Original')
              : undefined
          }
          onOriginalCoverUpload={onOriginalCoverUpload}
        />
      </div>
    );
  }

  return (
    <div className="book-dashboard-tab" data-tour-id="dashboard-container">
      <LanguageWorkspaceDashboard
        language={workspaceLanguage}
        bookTitle={book?.title?.trim() || 'Untitled book'}
        bookAuthor={book?.author?.trim() || 'Author not set'}
        summaryText={workspaceSummaryText}
        onBack={onReturnToOriginalDashboard}
        readinessLabel={languageReadiness.label}
        readinessTone={languageReadiness.tone}
        workstreams={languageWorkstreams}
        recommendedStep={recommendedStep}
        quickActions={translatedDashboardQuickActions}
        coverUrl={workspaceLanguageCoverUrl || workspaceFallbackCoverUrl}
        isUsingSourceCover={!workspaceLanguageCoverUrl && Boolean(workspaceFallbackCoverUrl)}
        coverInitials={workspaceCoverInitials}
        translatedCount={workspaceTranslatedCount}
        totalChapters={workspaceTotalChapters}
        proofreadCount={workspaceProofreadCount}
        recentExports={workspaceRecentExports}
        renderExportCard={renderExportCard}
        onOpenCoverGeneration={
          onOpenCoverTranslationForLanguage
            ? () => onOpenCoverTranslationForLanguage(workspaceLanguage)
            : undefined
        }
        onOpenCoverHistory={
          onOpenCoverHistoryForLanguage
            ? () => onOpenCoverHistoryForLanguage(workspaceLanguage)
            : undefined
        }
        renderDismissControl={renderDismissControl}
      />
    </div>
  );
};

export default BookDashboardTab;
